import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Search, Filter, Loader2, Clock, RotateCcw, Ban, MessageCircle, CheckCircle2,
    Users, FileText, ExternalLink, Phone, FileWarning, ShieldAlert, Gavel,
    FileSignature, AlertTriangle, UserPlus, UserMinus, NotebookPen, ChevronDown,
    Inbox, X, Mail, Send, MailWarning, Columns3, Table as TableIcon,
    List as ListIcon, CalendarDays, Route as RouteIcon, CalendarClock, BadgeCheck,
    Pencil, Trash2,
} from 'lucide-react';
import {
    fetchEsteira, filtrarDemandas, procurarClientesCobranca, trabalhoDoDia, kpisDaEsteira, contarDemandasDoUsuario, contarAgendados, contarDistrato,
    assumirDemanda, registrarAndamento, liberarDemanda, buscarUnidadePorNumero,
    editarAndamento, excluirAndamento, podeCorrigirAndamento,
    registrarContatoCliente, pendenciasDaRegua, assumirCliente, registrarRetiradaNegativacao,
} from '../../../src/services/esteiraCobranca';
import {
    donoEfetivo, bloqueadoresInternos, destinatariosCobrancaDistrato, demandasTravadasPor, compartilhadaCom,
    ehDaRegua, ehDeDocumento, trabalhadaHoje, estaParado,
} from './normalizar';
import QuadroEtapas from './QuadroEtapas';
import TabelaDemandas from './TabelaDemandas';
import UcsDoCard from './UcsDoCard';
import CalendarioDemandas from './CalendarioDemandas';
import ResumoGerencial from './ResumoGerencial';
import {
    registrarEnvioDocumento, registrarNaoEnvioDocumento, enviarLembreteAssinatura,
    cancelarDocumentoJuridico, docTipoDaOrigem,
    type DocTipoEnvio,
} from '../../../src/services/documentosJuridicos';
import AndamentoModal from './AndamentoModal';
import AcaoDocumentoModal, { type AcaoDocumento } from './AcaoDocumentoModal';
// Emissão de documento. Os três aceitam `unidade` opcional e têm busca de UC
// embutida, então abrem direto daqui — a pessoa não precisa achar a UC em outra
// tela só para gerar. É o "elaboração" do pedido original.
import NotificacaoMoraModal from '../NotificacaoMoraModal';
import ComunicadoAdvertenciaModal from '../ComunicadoAdvertenciaModal';
import CancelUCModal from '../CancelUCModal';
import UCManagementModal from '../UCManagementModal';
import {
    registrarAcaoManual, dispensarAcaoManual, reabrirAcaoManual, api,
    fetchResponsaveisPorEtapa, setResponsaveisPorEtapa, redistribuirPendentesDaEtapa,
    type ResponsaveisPorEtapa as MapaResponsaveis,
} from '../../../src/services/api';
import type {
    DemandaEsteira, EsteiraFila, EsteiraGrupo, CobrancaAcaoManual, CobrancaAcaoEtapa,
    CobrancaAcaoCanal, CobrancaAcaoStatus, CobrancaStatusNegociacao, Profile,
} from '../../../types';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { temDepartamento } from '../../../src/lib/departamentos';
import { logUsageEvent } from '../../../src/lib/logger';
import { guardarPreselecao, ucsDasDemandas } from '../../../src/lib/preselecaoRota';
import { derivarEstadoNotificacaoD17 } from '../../../src/lib/derivarEstadoNotificacaoD17';
import ClienteCobrancaModal from '../../../src/components/ClienteCobrancaModal';
import { STATUS_NEGOCIACAO_META } from '../../../src/components/PainelNegociacaoCobranca';
import RegistrarAcaoModal, {
    type AlvoRegistro, type DadosRegistro, type ModoRegistro,
} from '../LembretesCobranca/RegistrarAcaoModal';
import { avisoDoDesfecho } from '../../../src/lib/desfechoContato';
import ResponsaveisPorEtapaCard from '../LembretesCobranca/ResponsaveisPorEtapa';
import { STATUS_ACAO_META, formatDia as formatDiaRegua } from '../LembretesCobranca/etapas';
import {
    FILAS, GRUPOS, ORDEM_FILAS, DEPARTAMENTOS_COBRANCA, formatBRL, formatDia,
} from './filas';
import {
    FILTROS_PADRAO, lerPosicao, gravarPosicao, ancoraVisivel, voltarParaPosicao,
    type Filtros,
} from './posicaoPainel';

const ICONE_GRUPO: Record<EsteiraGrupo, React.ReactNode> = {
    regua: <Phone className="w-4 h-4" />,
    notificacoes: <FileWarning className="w-4 h-4" />,
    documentos: <FileSignature className="w-4 h-4" />,
    negativacao: <Gavel className="w-4 h-4" />,
    negociacao: <Users className="w-4 h-4" />,
};

const ICONE_FILA: Partial<Record<EsteiraFila, React.ReactNode>> = {
    regua_d4: <Phone size={13} />,
    regua_d10: <Phone size={13} />,
    regua_d17: <FileWarning size={13} />,
    regua_d24: <ShieldAlert size={13} />,
    sem_retorno: <CalendarClock size={13} />,
};

// 1.117 cards de uma vez é parede, não fila com prioridade — a lista corta em
// páginas de 30.
const TAMANHO_PAGINA = 30;

/**
 * Os cinco modos respondem a perguntas diferentes sobre a MESMA fila filtrada —
 * não são estilos do mesmo conteúdo. Cada um tem dono:
 *
 *   Lista      → Bruna, trabalhando caso a caso
 *   Etapas     → onde o fluxo empoça
 *   Tabela     → triagem em volume e export
 *   Calendário → prazos e o dia em que o atraso começou
 *   Resumo     → a gerente, que não trabalha fila mas precisa saber se o time dá conta
 *
 * ⚠️ Não há modo "por quem trava": só o distrato tem lista nominal de
 * pendentes; nas outras 13 filas o modo degeneraria em "agrupado por uma
 * pessoa". Essa informação virou o KPI de bloqueio interno, uma coluna da
 * tabela e um bloco do Resumo, onde ela é honesta.
 */
type Modo = 'lista' | 'etapas' | 'tabela' | 'calendario' | 'resumo';

const MODOS: Array<{ modo: Modo; rotulo: string; icone: React.ReactNode; ajuda: string }> = [
    { modo: 'lista', rotulo: 'Lista', icone: <ListIcon size={16} />, ajuda: 'Trabalhar caso a caso, com todas as ações à mão' },
    { modo: 'etapas', rotulo: 'Quadro', icone: <Columns3 size={16} />, ajuda: 'Ver em que fase do processo o trabalho está empoçando' },
    { modo: 'tabela', rotulo: 'Tabela', icone: <TableIcon size={16} />, ajuda: 'Triar em volume, ordenar e exportar' },
    { modo: 'calendario', rotulo: 'Calendário', icone: <CalendarDays size={16} />, ajuda: 'Ver prazos e o dia em que cada demanda parou' },
    { modo: 'resumo', rotulo: 'Resumo', icone: <FileText size={16} />, ajuda: 'Quem está com o quê, e há quanto tempo' },
];

/**
 * Painel de Cobrança e Documentos.
 *
 * ⚠️ O nome no código é outro, e é de propósito: pasta, tipos (`DemandaEsteira`),
 * funções (`fetchEsteira`), a rota `/financeiro/esteira` e a tabela
 * `esteira_atribuicoes` — já aplicada em produção — falam "esteira", que foi o
 * nome de trabalho. O nome que o usuário vê é "Painel de Cobrança e Documentos"
 * (escolhido em 11/09/2026). Renomear o código custaria uma migration de tabela
 * e um diff enorme sem mudar nada para quem usa, então ficou assim. Ao mexer
 * aqui: "esteira" é a coisa, "Painel de Cobrança e Documentos" é como ela se
 * chama na tela.
 *
 * Junta numa fila só o que hoje só chega pelo sininho: régua de contato humano,
 * notificação de mora, comunicado de advertência, distrato, negativação e
 * retorno de negociação. O sino avisa mas não guarda estado — lida não é feita.
 * Aqui cada demanda tem fila, responsável, idade e desfecho.
 *
 * A tela não cria nada: toda linha é derivada do estado das tabelas de origem, e
 * as ações reabrem os modais que já existem. Ver `normalizar.ts` para as regras
 * de cada fila.
 */
const EsteiraCobrancaView: React.FC = () => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const [todas, setTodas] = useState<DemandaEsteira[]>([]);
    const [loading, setLoading] = useState(true);
    const [perfis, setPerfis] = useState<Profile[]>([]);

    // Fila e grupo podem vir do link da notificação (?fila=mora_sem_envio,
    // ?grupo=documentos) — é a ponte que faz o sininho deixar de ser o lugar
    // onde se trabalha. O grupo atende os avisos que não apontam para uma fila
    // única: "documento assinado" pode ser mora ou advertência.
    const filaDaUrl = (searchParams.get('fila') as EsteiraFila | null) ?? '';
    const grupoDaUrl = (searchParams.get('grupo') as EsteiraGrupo | null) ?? '';
    const veioDeLink = !!filaDaUrl || !!grupoDaUrl;

    // Onde a pessoa estava da última vez que saiu desta tela, nesta aba. Lido
    // uma vez só, na montagem — ver `posicaoPainel.ts`. O ProtectedRoute só
    // monta a tela com `user` carregado, então o id já está aqui.
    const [salvo] = useState(() => user
        ? lerPosicao({ usuarioId: user.id, filaDaUrl, grupoDaUrl })
        : null);

    const [filtros, setFiltros] = useState<Filtros>(() => salvo?.filtros ?? {
        ...FILTROS_PADRAO,
        fila: FILAS[filaDaUrl as EsteiraFila] ? filaDaUrl : '',
        grupo: GRUPOS.some(g => g.grupo === grupoDaUrl) ? grupoDaUrl : '',
    });

    // O modo vive na URL junto com os filtros: assim um link colado no WhatsApp
    // abre a mesma coisa que a pessoa estava vendo, e o voltar do navegador
    // desfaz a troca de modo como desfaz um filtro.
    const modoDaUrl = searchParams.get('modo') as Modo | null;
    const modo: Modo = MODOS.some(m => m.modo === modoDaUrl) ? modoDaUrl! : 'lista';
    const trocarModo = (novo: Modo) => {
        const proximos = new URLSearchParams(searchParams);
        if (novo === 'lista') proximos.delete('modo'); else proximos.set('modo', novo);
        setSearchParams(proximos, { replace: true });
        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: `modo_${novo}`, origem: 'acao' });
    };

    // Voltando pelo menu a URL chega limpa: devolve para ela a fila e o modo
    // restaurados, senão a tela mostraria um recorte que a URL não descreve.
    // O modo só volta quando a pessoa entrou pelo menu — um link do sininho
    // é para trabalhar, e isso é na Lista.
    useEffect(() => {
        if (!salvo) return;
        const proximos = new URLSearchParams(searchParams);
        let mudou = false;
        if (salvo.filtros.fila && !filaDaUrl) {
            proximos.set('fila', salvo.filtros.fila);
            mudou = true;
        }
        if (!veioDeLink && !modoDaUrl && salvo.modo !== 'lista' && MODOS.some(m => m.modo === salvo.modo)) {
            proximos.set('modo', salvo.modo);
            mudou = true;
        }
        if (mudou) setSearchParams(proximos, { replace: true });
        // Só na montagem: depois disso quem mexe na URL são os próprios controles.
    }, []);

    const [modal, setModal] = useState<{ demanda: DemandaEsteira; modo: ModoRegistro } | null>(null);
    const [clienteAberto, setClienteAberto] = useState<DemandaEsteira | null>(null);
    /** Clientes trabalhados hoje no "Gerenciar Cobrança", com ou sem card (#6211). */
    const [trabalhoDia, setTrabalhoDia] = useState<Awaited<ReturnType<typeof trabalhoDoDia>>>([]);
    const [andamentoAberto, setAndamentoAberto] = useState<{ demanda: DemandaEsteira; modo: 'registrar' | 'corrigir' } | null>(null);
    const [acaoDoc, setAcaoDoc] = useState<{ demanda: DemandaEsteira; acao: AcaoDocumento } | null>(null);
    /** Qual documento está sendo emitido, se algum. */
    const [emitindo, setEmitindo] = useState<'mora' | 'advertencia' | 'distrato' | null>(null);
    /** UC aberta em detalhe pelo clique no nome do cliente. */
    const [ucAberta, setUcAberta] = useState<any | null>(null);
    const [buscandoUc, setBuscandoUc] = useState<string | null>(null);

    // Assumir/devolver recarrega a esteira inteira (~5 queries, 1.117 linhas) —
    // até a resposta voltar o card continua na tela e o botão continua clicável.
    // Sem isto um segundo clique duplica a chamada.
    const [demandasEmAcao, setDemandasEmAcao] = useState<Set<string>>(new Set());

    // Pool de responsáveis por etapa da régua — herdado da ManuaisTab, que a
    // esteira absorve. Sem isto a migração seria regressão: é aqui que se decide
    // quem recebe as etapas do dia.
    const [mapaResponsaveis, setMapaResponsaveis] = useState<MapaResponsaveis>({ 'D+4': [], 'D+10': [], 'D+17': [], 'D+24': [] });
    const [salvandoEtapa, setSalvandoEtapa] = useState<CobrancaAcaoEtapa | null>(null);

    const perfisElegiveis = useMemo(
        () => perfis.filter(p =>
            (p.status ?? 'active') !== 'inactive' &&
            DEPARTAMENTOS_COBRANCA.some(d => temDepartamento(p, d))
        ),
        [perfis]
    );

    const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
    const [atualizando, setAtualizando] = useState(false);
    const atualizadoEmRef = useRef<Date | null>(null);

    /**
     * `silencioso` recarrega por baixo, sem trocar a lista pelo spinner — é o
     * modo da atualização automática e de toda recarga depois de uma ação.
     * Trocar a lista por "Carregando" encolhe a página e o navegador joga a
     * rolagem para o topo: a pessoa perdia o card em que estava trabalhando a
     * cada modal fechado. O spinner fica só para a primeira carga e para a
     * troca do filtro de status, em que a lista anterior não vale mais.
     */
    const carregar = useCallback(async (silencioso = false) => {
        if (silencioso) setAtualizando(true); else setLoading(true);
        try {
            const [lista, dia] = await Promise.all([
                fetchEsteira({ status: filtros.status }),
                trabalhoDoDia().catch(() => null),
            ]);
            setTodas(lista);
            if (dia) setTrabalhoDia(dia);
            const agora = new Date();
            atualizadoEmRef.current = agora;
            setAtualizadoEm(agora);
        } catch (e: any) {
            // Falha em segundo plano não merece toast a cada tentativa: a tela
            // continua com o último dado bom e o horário de atualização envelhece.
            if (!silencioso) addToast(e?.message || 'Erro ao carregar o painel', 'error');
        } finally {
            if (silencioso) setAtualizando(false); else setLoading(false);
        }
    }, [filtros.status, addToast]);

    useEffect(() => { carregar(); }, [carregar]);

    /**
     * A tela se atualiza sozinha. Várias pessoas trabalham as mesmas filas
     * (Bruna, João, Dhaiane): sem isto, quem ficou com a aba aberta via demanda
     * que o colega já tinha resolvido, e trabalhava de novo.
     *
     * Atualiza a cada 5 minutos com a aba visível, e ao voltar para a aba se o
     * dado tiver mais de 1 minuto. Aba escondida não consulta nada — 5 tabelas
     * a cada poucos minutos, por pessoa, não é de graça.
     *
     * Não usa Realtime de propósito: as tabelas de origem não estão na
     * publicação e isso exigiria mudança de banco.
     */
    useEffect(() => {
        const CINCO_MIN = 5 * 60_000;
        const talvezAtualizar = () => {
            if (document.visibilityState !== 'visible') return;
            const ultimo = atualizadoEmRef.current;
            if (ultimo && Date.now() - ultimo.getTime() < 60_000) return;
            carregar(true);
        };
        const intervalo = window.setInterval(talvezAtualizar, CINCO_MIN);
        document.addEventListener('visibilitychange', talvezAtualizar);
        window.addEventListener('focus', talvezAtualizar);
        return () => {
            window.clearInterval(intervalo);
            document.removeEventListener('visibilitychange', talvezAtualizar);
            window.removeEventListener('focus', talvezAtualizar);
        };
    }, [carregar]);

    /**
     * Registra no andamento que a cobrança saiu. O e-mail abre no computador da
     * pessoa e o sistema não sabe se foi enviado — mas sabe que alguém decidiu
     * cobrar, quem e quando. Antes, clicar em "Cobrar" não deixava rastro nenhum
     * e ninguém conseguia dizer se a Dhaiane tinha sido cobrada.
     *
     * `assumirSeLivre: false`: cobrar não é assumir. Um clique no card da
     * Dhaiane registra em todos os documentos dela sem tornar quem clicou dono.
     */
    const registrarCobranca = async (alvos: DemandaEsteira[], texto: string) => {
        if (!user || !alvos.length) return;
        const resultados = await Promise.allSettled(
            alvos.map(d => registrarAndamento(d, user.id, texto, { assumirSeLivre: false }))
        );
        const falhas = resultados.filter(r => r.status === 'rejected').length;
        if (falhas) {
            addToast(`Cobrança aberta, mas ${falhas} de ${alvos.length} registro(s) não foram salvos.`, 'error');
        } else {
            addToast(
                alvos.length > 1
                    ? `Cobrança registrada em ${alvos.length} documentos.`
                    : 'Cobrança registrada no andamento.',
                'success'
            );
        }
        logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: 'cobrar_assinatura_email', origem: 'acao' });
        carregar(true);
    };

    useEffect(() => {
        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'esteira', origem: 'navegacao' });
    }, [user?.id]);

    useEffect(() => {
        let cancelado = false;
        Promise.all([api.auth.listProfiles(), fetchResponsaveisPorEtapa()])
            .then(([lista, mapa]) => {
                if (cancelado) return;
                setPerfis(lista as Profile[]);
                setMapaResponsaveis(mapa);
            })
            .catch(() => { /* a esteira continua utilizável sem o seletor */ });
        return () => { cancelado = true; };
    }, []);

    const trocarPoolDaEtapa = async (etapa: CobrancaAcaoEtapa, pool: string[]) => {
        const anterior = mapaResponsaveis;
        const novo = { ...mapaResponsaveis, [etapa]: pool };
        setMapaResponsaveis(novo);          // otimista: o card responde na hora
        setSalvandoEtapa(etapa);
        try {
            await setResponsaveisPorEtapa(novo);
            // As etapas que ninguém tocou ainda acompanham a troca — senão a
            // mudança só valeria amanhã e a fila de hoje ficaria com quem saiu.
            const movidas = await redistribuirPendentesDaEtapa(etapa, pool);
            addToast(
                movidas > 0
                    ? `${etapa} atualizado — ${movidas} pendente(s) redistribuído(s).`
                    : `${etapa} atualizado.`,
                'success'
            );
            carregar(true);
        } catch (e: any) {
            setMapaResponsaveis(anterior);  // desfaz o otimismo
            addToast(e?.message || 'Erro ao salvar os responsáveis', 'error');
        } finally {
            setSalvandoEtapa(null);
        }
    };

    // Quem tem fila cai direto na própria; quem não tem (gestão) vê tudo. Mesma
    // regra da fila de contato humano — só que aqui a contagem já está em
    // memória, sem precisar de uma query extra.
    // Quem voltou para a tela já tem o próprio recorte restaurado — o padrão
    // não passa por cima dele.
    const [aplicouPadrao, setAplicouPadrao] = useState(!!salvo);
    // Quem faz o contato (D+4 e D+10). Entre essas pessoas o retorno agendado
    // e o cliente sem dono são de todas — ver `compartilhadaCom`.
    const poolContato = useMemo(
        () => [...new Set([...(mapaResponsaveis['D+4'] ?? []), ...(mapaResponsaveis['D+10'] ?? [])])],
        [mapaResponsaveis]
    );
    const minhasPendentes = useMemo(
        () => (user ? contarDemandasDoUsuario(todas, user.id, poolContato) : 0),
        [todas, user, poolContato]
    );
    useEffect(() => {
        if (aplicouPadrao || loading || !user || !todas.length) return;
        setAplicouPadrao(true);
        // Se o link já trouxe fila ou grupo, a pessoa clicou querendo ver aquilo
        // — não sequestra o filtro.
        if (minhasPendentes > 0 && !veioDeLink) {
            setFiltros(f => (f.responsavelId ? f : { ...f, responsavelId: user.id }));
        }
    }, [aplicouPadrao, loading, user, todas.length, minhasPendentes, veioDeLink]);

    // O que é do distrato fica fora da lista e dos números, menos quando a
    // pessoa liga o filtro dele — aí só ele conta.
    const base = useMemo(
        () => todas.filter(d => !!d.com_distrato === filtros.apenasDistrato),
        [todas, filtros.apenasDistrato]
    );
    const kpis = useMemo(() => kpisDaEsteira(base), [base]);
    // Trabalhadas hoje = clientes do histórico do dia + cards com trabalho (régua,
    // documento) — um por cliente. Contar só os cards perdia quem saiu do painel.
    const cpfDe = (d: DemandaEsteira) => (d.cpf_cnpj_cliente ?? '').replace(/\D/g, '') || d.id;
    const nTrabalhadas = useMemo(() => {
        const agora = new Date();
        const chaves = new Set(trabalhoDia.map(t => t.cpf));
        for (const d of base) if (trabalhadaHoje(d, agora)) chaves.add(cpfDe(d));
        return chaves.size;
    }, [base, trabalhoDia]);
    // Lista "Também trabalhados": respeita busca e demais recortes; sem isso mostrava
    // gente que a pessoa tinha filtrado fora, sob o aviso de "sem pendência no painel".
    const diaFiltrado = useMemo(() => {
        if (!filtros.apenasTrabalhadasHoje) return [];
        if (filtros.responsavelId || filtros.grupo || filtros.fila || filtros.statusNegociacao
            || filtros.apenasSemDono || filtros.apenasDistrato || filtros.minDiasParado > 0) return [];
        const termo = filtros.search.trim().toLowerCase();
        const dig = termo.replace(/\D/g, '');
        return trabalhoDia.filter(t => !termo || (t.nome ?? '').toLowerCase().includes(termo) || (dig.length >= 3 && t.cpf.includes(dig)));
    }, [filtros, trabalhoDia]);
    // Negativados só para acompanhar (~100): ficam fora do número de trabalho do grupo
    // Negativação e do total de pendentes, para não parecerem tarefas.
    const nAcompanhamento = useMemo(
        () => base.filter(d => d.status === 'pendente' && !d.agendado && d.acompanhamento).length,
        [base]
    );
    // Quem da Vigor está segurando documento. É o dado mais acionável do painel:
    // uma mensagem para a pessoa do topo destrava dezenas de processos.
    // E-mail corporativo → nome do perfil: separa pessoa de caixa compartilhada
    // (assinatura@, contato@), que o Autentique rotula com nome de cliente.
    const nomePorEmail = useMemo(() => new Map(
        perfis
            .filter(p => (p as any).email_corporativo)
            .map(p => [String((p as any).email_corporativo).trim().toLowerCase(), p.full_name as string])
    ), [perfis]);
    const bloqueadores = useMemo(() => bloqueadoresInternos(base, nomePorEmail), [base, nomePorEmail]);
    const visiveis = useMemo(() => filtrarDemandas(todas, filtros, poolContato), [todas, filtros, poolContato]);
    // De quem é a lista que está na tela: a pessoa filtrada ou quem está logado.
    // Sem isto, a gerente que filtra pela Dhaiane via os retornos do João sem
    // saber por que eles estavam ali.
    const quemVe = filtros.responsavelId || user?.id || '';
    const nomeDeQuemVe = filtros.responsavelId && filtros.responsavelId !== user?.id
        ? (perfis.find(p => String(p.id) === filtros.responsavelId)?.full_name?.split(' ')[0] ?? 'a pessoa')
        : null;
    const vendoAsMinhas = !!user && filtros.responsavelId === user.id;

    // Reabre do zero quando o RECORTE muda — senão "carregar mais" ia
    // acumulando itens de um filtro que a pessoa já trocou. Recarregar os dados
    // não conta: isto já dependeu de `todas`, e toda atualização (a automática,
    // a de voltar para a aba, a de depois de salvar) cortava a lista de volta
    // para 30 — quem estava no card 45 perdia o card.
    const [qtdVisivel, setQtdVisivel] = useState(salvo?.qtdVisivel ?? TAMANHO_PAGINA);
    const chaveRecorte = JSON.stringify(filtros);
    const recorteAnterior = useRef(chaveRecorte);
    useEffect(() => {
        if (recorteAnterior.current === chaveRecorte) return;
        recorteAnterior.current = chaveRecorte;
        setQtdVisivel(TAMANHO_PAGINA);
    }, [chaveRecorte]);
    const paginadas = useMemo(() => visiveis.slice(0, qtdVisivel), [visiveis, qtdVisivel]);

    // -----------------------------------------------------------------------
    // Voltar para onde a pessoa estava
    //
    // Dentro da tela a posição já se mantém: as recargas são silenciosas e a
    // paginação não zera. Isto cobre sair para outra tela do VigorHub e
    // voltar — a tela desmonta e perderia filtro, páginas e rolagem. Grava
    // enquanto a pessoa rola e reaplica quando a lista termina de carregar.
    // -----------------------------------------------------------------------
    const listaRef = useRef<HTMLDivElement>(null);
    const restaurarRef = useRef(salvo);
    // Até restaurar, a tela é o spinner: gravar ali trocaria a posição boa por "topo".
    const podeGravarRef = useRef(false);
    const estadoRef = useRef({ filtros, qtdVisivel, modo });
    estadoRef.current = { filtros, qtdVisivel, modo };

    const gravarPosicaoAtual = useCallback(() => {
        if (!user?.id || !podeGravarRef.current) return;
        const atual = estadoRef.current;
        gravarPosicao({
            usuarioId: user.id,
            salvoEm: Date.now(),
            filtros: atual.filtros,
            qtdVisivel: atual.qtdVisivel,
            modo: atual.modo,
            scrollY: window.scrollY,
            ancora: atual.modo === 'lista' ? ancoraVisivel(listaRef.current) : null,
        });
    }, [user?.id]);

    useEffect(() => {
        if (loading) return;
        const pendente = restaurarRef.current;
        restaurarRef.current = null;
        if (pendente && pendente.modo === modo) {
            // Um frame para a lista já estar pintada com a altura final.
            requestAnimationFrame(() => {
                voltarParaPosicao(listaRef.current, pendente);
                podeGravarRef.current = true;
            });
        } else {
            podeGravarRef.current = true;
        }
    }, [loading]);

    // Layout effect de propósito: a limpeza roda com a lista ainda no DOM, e
    // dá para gravar o último rolar que a folga de 150 ms ainda não gravou.
    useLayoutEffect(() => {
        let timer: number | undefined;
        const aoRolar = () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(() => { timer = undefined; gravarPosicaoAtual(); }, 150);
        };
        window.addEventListener('scroll', aoRolar, { passive: true });
        return () => {
            window.removeEventListener('scroll', aoRolar);
            if (timer !== undefined) {
                window.clearTimeout(timer);
                gravarPosicaoAtual();
            }
        };
    }, [gravarPosicaoAtual]);

    useEffect(() => { gravarPosicaoAtual(); }, [chaveRecorte, qtdVisivel, modo, gravarPosicaoAtual]);

    // -----------------------------------------------------------------------
    // Montar rota de cobrança a partir do painel
    //
    // Era a barra da aba "Manuais" de Lembretes de Cobrança, que este painel
    // absorveu. Marca-se o card (o cliente) e a rota leva TODAS as UCs dele —
    // a parada da rota é a unidade. A seleção vive por id de demanda, então
    // sobrevive a "carregar mais" e à atualização automática.
    // -----------------------------------------------------------------------
    const [paraRota, setParaRota] = useState<Set<string>>(new Set());

    const alternarNaRota = useCallback((id: string) => setParaRota(atual => {
        const novo = new Set(atual);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        return novo;
    }), []);

    const ucsDaRota = useMemo(
        () => ucsDasDemandas(todas.filter(d => paraRota.has(d.id))),
        [todas, paraRota]);

    const gerarRotaDaSelecao = () => {
        if (ucsDaRota.length === 0) return;
        guardarPreselecao(ucsDaRota, 'seleção do Painel de Cobrança');
        // A Central de Inadimplência não é rota própria — abre como overlay por
        // este evento, que App.tsx escuta. A aba 'rota' é a de montagem.
        window.dispatchEvent(new CustomEvent('vigorhub:open-inadimplencia', { detail: { tab: 'rota' } }));
        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: 'gerar_rota_cobranca', origem: 'acao' });
        addToast(`${ucsDaRota.length} unidade(s) enviadas para o agendamento de visita. Escolha quem vai e onde a rota começa e termina.`, 'success');
        setParaRota(new Set());
    };

    const trocarFila = (fila: EsteiraFila | '') => {
        setFiltros(f => ({ ...f, fila }));
        const proximos = new URLSearchParams(searchParams);
        if (fila) proximos.set('fila', fila); else proximos.delete('fila');
        setSearchParams(proximos, { replace: true });
    };

    /**
     * Registro do card. É do CLIENTE, não da etapa em que se clicou:
     *
     *   • contato — com CPF, vai pela função do banco, que grava data, próximo
     *     contato e observação e fecha as etapas de contato (D+4/D+10) de todo
     *     mundo. Sem CPF não há carteira: fecha as etapas de contato do card;
     *   • conferência — fecha o(s) D+24 do card;
     *   • dispensa — tira todas as etapas pendentes do card.
     */
    const confirmarRegistro = async (dados: DadosRegistro) => {
        if (!modal || !user) return;
        const d = modal.demanda;
        const pendentes = pendenciasDaRegua(d);
        const registrarNasEtapas = (etapas: CobrancaAcaoManual[]) => Promise.all(etapas.map(a =>
            registrarAcaoManual({ acaoId: a.id, userId: user.id, canal: dados.canal, dataExecucao: dados.data, observacao: dados.observacao })
        ));
        try {
            const comCarteira = modal.modo !== 'conferencia' && !!d.cpf_cnpj_cliente;
            if (modal.modo === 'conferencia') {
                await registrarNasEtapas(pendentes.filter(a => a.etapa === 'D+24'));
            } else if (d.cpf_cnpj_cliente) {
                await registrarContatoCliente(d.cpf_cnpj_cliente, d.cliente_nome, dados, {
                    id: user.id,
                    nome: profile?.full_name || user.email || 'Sistema',
                });
            } else {
                await registrarNasEtapas(pendentes.filter(a => a.etapa === 'D+4' || a.etapa === 'D+10'));
            }
            logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: modal.modo === 'conferencia' ? 'registrar_conferencia' : 'registrar_contato', origem: 'acao' });
            addToast(
                modal.modo === 'conferencia' ? 'Conferência registrada.'
                    : comCarteira ? `Contato registrado. ${avisoDoDesfecho(dados.desfecho) ?? ''}`.trim()
                        : 'Contato registrado.',
                'success'
            );
            setModal(null);
            carregar(true);
        } catch (e: any) {
            addToast(e?.message || 'Erro ao registrar', 'error');
        }
    };

    const confirmarDispensa = async (motivo: string) => {
        if (!modal || !user) return;
        const pendentes = pendenciasDaRegua(modal.demanda);
        try {
            await Promise.all(pendentes.map(a => dispensarAcaoManual(a.id, user.id, motivo)));
            addToast(pendentes.length > 1 ? `${pendentes.length} etapas dispensadas.` : 'Etapa dispensada.', 'success');
            setModal(null);
            carregar(true);
        } catch (e: any) {
            addToast(e?.message || 'Erro ao dispensar', 'error');
        }
    };

    /** Negativado que regularizou: a Bruna tira do SPC e registra aqui. */
    const registrarRetirada = (d: DemandaEsteira) => {
        if (!user) return;
        const linha = (d.itens ?? [d]).find(i => i.fila === 'negativacao_retirar');
        if (!linha) return;
        const confirmado = window.confirm(
            `Confirma que a negativação de ${d.cliente_nome || 'este cliente'} já foi retirada do SPC/Serasa?`
        );
        if (!confirmado) return;
        comBloqueio(d.id, () => comFeedback(
            () => registrarRetiradaNegativacao(
                linha.origem_id,
                d.status_negociacao ?? 'pago',
                user.id,
                profile?.full_name || user.email || 'Sistema',
            ),
            'Retirada da negativação registrada.'
        ));
        logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: 'retirar_negativacao', origem: 'acao' });
    };

    const reabrir = async (acao: CobrancaAcaoManual) => {
        try {
            await reabrirAcaoManual(acao.id);
            addToast('Etapa devolvida para a fila.', 'success');
            carregar(true);
        } catch (e: any) {
            addToast(e?.message || 'Erro ao reabrir a etapa', 'error');
        }
    };

    /** Assumir, repassar e devolver: as três só valem nas filas derivadas. */
    const comFeedback = async (acao: () => Promise<void>, ok: string) => {
        try {
            await acao();
            addToast(ok, 'success');
            carregar(true);
        } catch (e: any) {
            addToast(
                e?.code === '42P01'
                    ? 'A tabela de atribuições ainda não existe no banco — aplique a migration esteira_atribuicoes.'
                    : e?.message || 'Não foi possível salvar',
                'error'
            );
        }
    };

    const comBloqueio = async (id: string, acao: () => Promise<void>) => {
        if (demandasEmAcao.has(id)) return; // segundo clique enquanto a primeira chamada ainda está no ar
        setDemandasEmAcao(prev => new Set(prev).add(id));
        try {
            await acao();
        } finally {
            setDemandasEmAcao(prev => { const p = new Set(prev); p.delete(id); return p; });
        }
    };

    const assumir = (d: DemandaEsteira) => {
        if (!user) return;
        const dono = donoEfetivo(d);
        // Já está com outra pessoa — numa equipe de 4 que se cobre em férias
        // isto é rotina, mas ainda é sobrescrever o trabalho de alguém, então
        // pede uma confirmação rápida em vez de trocar o dono num clique só.
        // Sem dono na carteira (só o último contato) não há carteira de ninguém para tirar.
        if (dono && dono !== user.id && !d.dono_pelo_contato) {
            const nome = ehDoCliente(d) ? d.responsavel_nome : d.atribuicao?.responsavel_nome;
            const confirmado = window.confirm(
                ehDoCliente(d)
                    ? `Este cliente está com ${nome ?? 'outra pessoa'}. Assumir passa a carteira inteira dele para você, não só uma etapa. Assumir mesmo assim?`
                    : `Esta demanda está com ${nome ?? 'outra pessoa'}. Assumir mesmo assim?`
            );
            if (!confirmado) return;
        }
        // Cobrança do cliente (régua, negociação, negativação): assumir é virar
        // dono na carteira — as etapas de contato vêm junto e saem da fila de
        // quem tinha. Documento continua com a atribuição da esteira.
        comBloqueio(d.id, () => comFeedback(
            () => ehDoCliente(d) && d.cpf_cnpj_cliente
                ? assumirCliente(d.cpf_cnpj_cliente, d.cliente_nome, user.id)
                : assumirDemanda(d, user.id, user.id),
            ehDoCliente(d) ? 'Cliente assumido — ele aparece em "Meus itens".' : 'Demanda assumida — ela aparece em "Meus itens".'
        ));
        logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: ehDoCliente(d) ? 'assumir_cliente' : 'assumir_demanda', origem: 'acao' });
    };

    const liberar = (d: DemandaEsteira) =>
        comBloqueio(d.id, () => comFeedback(() => liberarDemanda(d), 'Demanda devolvida para a fila aberta.'));

    const confirmarAndamento = async (observacao: string) => {
        if (!andamentoAberto || !user) return;
        const { demanda, modo } = andamentoAberto;
        const atribuicao = demanda.atribuicao;
        if (modo === 'corrigir') {
            if (!atribuicao) return;
            await comFeedback(() => editarAndamento(atribuicao, user.id, observacao), 'Anotação corrigida.');
        } else {
            await comFeedback(() => registrarAndamento(demanda, user.id, observacao), 'Andamento registrado.');
        }
        logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: modo === 'corrigir' ? 'corrigir_andamento' : 'registrar_andamento', origem: 'acao' });
        setAndamentoAberto(null);
    };

    /** Anotação preenchida errado (#6290): some do card, quem assumiu continua. */
    const excluirAnotacao = (d: DemandaEsteira) => {
        const atribuicao = d.atribuicao;
        if (!user || !atribuicao?.ultima_acao_em) return;
        const deOutro = atribuicao.ultima_acao_por !== user.id;
        const confirmado = window.confirm(
            `Excluir a anotação${deOutro ? ` de ${atribuicao.ultima_acao_por_nome ?? 'outra pessoa'}` : ''}?\n\n`
            + `"${atribuicao.observacao ?? ''}"\n\n`
            + 'Ela some do card e não dá para desfazer. Quem assumiu a demanda continua com ela.'
        );
        if (!confirmado) return;
        comBloqueio(d.id, () => comFeedback(() => excluirAndamento(atribuicao), 'Anotação excluída.'));
        logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: 'excluir_andamento', origem: 'acao' });
    };

    /**
     * Ações de documento — é o "trabalhar nelas" do pedido original. Antes o
     * painel só sabia mandar a pessoa para a tela de UCs.
     */
    const executarAcaoDoc = async (valor: string) => {
        if (!acaoDoc || !user) return;
        const { demanda, acao } = acaoDoc;
        const tipo = docTipoDaOrigem(demanda.origem);
        if (!tipo) return;

        try {
            const r = acao === 'registrar_envio'
                ? await registrarEnvioDocumento(demanda.origem_id, tipo as DocTipoEnvio, valor, user.id)
                : acao === 'nao_enviar'
                    ? await registrarNaoEnvioDocumento(demanda.origem_id, tipo as DocTipoEnvio, valor, user.id)
                    : await cancelarDocumentoJuridico(demanda.origem_id, tipo, valor);

            addToast(r.mensagem, r.ok ? 'success' : 'error');
            if (r.ok) {
                logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'esteira', acao, origem: 'acao' });
                setAcaoDoc(null);
                carregar(true);
            }
        } catch (e: any) {
            addToast(e?.message || 'Não foi possível concluir a ação.', 'error');
        }
    };

    const lembrarAssinatura = async (d: DemandaEsteira) => {
        const tipo = docTipoDaOrigem(d.origem);
        // O distrato não entra: a edge function de lembrete só conhece mora e
        // advertência (ver TABELAS em enviar-lembrete-assinatura).
        if (tipo !== 'mora' && tipo !== 'advertencia') return;
        const r = await enviarLembreteAssinatura(d.origem_id, tipo);
        addToast(r.mensagem, r.ok ? 'success' : 'info');
        if (r.ok) {
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: 'enviar_lembrete_assinatura', origem: 'acao' });
            carregar(true);
        }
    };

    /**
     * Clique no nome do cliente: abre o detalhe mais útil para aquela demanda.
     *
     * Documento (mora, advertência, distrato) abre a **UC** — é lá que estão o
     * cadastro, as faturas e o histórico dos documentos daquele cliente, que é o
     * contexto de quem está elaborando. As demais filas abrem a **negociação**,
     * onde mora a timeline de cobrança.
     *
     * Se a UC não tiver cadastro (acontece: número novo da distribuidora que
     * nunca foi recadastrado), cai na negociação em vez de não fazer nada.
     */
    const abrirDetalhe = async (d: DemandaEsteira) => {
        const ehDocumento = ehDeDocumento(d);
        if (ehDocumento && d.uc_numero) {
            setBuscandoUc(d.id);
            try {
                const uc = await buscarUnidadePorNumero(d.uc_numero);
                if (uc) { setUcAberta(uc); return; }
                addToast(`UC ${d.uc_numero} não tem cadastro — abrindo a negociação.`, 'info');
            } catch {
                addToast('Não foi possível abrir a UC. Abrindo a negociação.', 'info');
            } finally {
                setBuscandoUc(null);
            }
        }
        if (d.cpf_cnpj_cliente) { setClienteAberto(d); return; }
        addToast('Esta demanda não tem CPF/CNPJ nem UC cadastrada para abrir.', 'info');
    };

    const limparFiltros = () => { setFiltros(FILTROS_PADRAO); trocarFila(''); };
    const temFiltroExtra = filtros.grupo || filtros.fila || filtros.responsavelId
        || filtros.search || filtros.minDiasParado > 0 || filtros.status !== 'pendente'
        || filtros.statusNegociacao || filtros.apenasAgendados || filtros.apenasDistrato || filtros.apenasTrabalhadasHoje;
    const agendados = useMemo(() => contarAgendados(todas), [todas]);
    const doDistrato = useMemo(() => contarDistrato(todas), [todas]);

    const inputCls = 'px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white';

    return (
        <div className="p-4 md:p-6 space-y-6">
            <div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Painel de Cobrança e Documentos</h1>

                    <div className="flex flex-wrap items-center gap-2">
                    {/* Emitir documento sem sair do painel. Antes a pessoa saía
                        para a tela de UCs achar o cliente — e "elaboração" é a
                        primeira palavra do pedido que originou esta tela. */}
                    {([
                        { tipo: 'mora' as const, rotulo: 'Notificação de mora' },
                        { tipo: 'advertencia' as const, rotulo: 'Advertência' },
                        { tipo: 'distrato' as const, rotulo: 'Distrato' },
                    ]).map(({ tipo, rotulo }) => (
                        <button
                            key={tipo}
                            onClick={() => {
                                setEmitindo(tipo);
                                logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'esteira', acao: `emitir_${tipo}`, origem: 'acao' });
                            }}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-primary border border-primary/25 hover:bg-primary/10 transition-colors flex items-center gap-1.5"
                            title={`Emitir ${rotulo.toLowerCase()} — a busca de UC fica dentro do formulário`}
                        >
                            <FileSignature size={13} /> {rotulo}
                        </button>
                    ))}
                    </div>

                    {/* Seletor de modo. Os três olham a MESMA fila já filtrada —
                        trocar de modo nunca muda o recorte, só a pergunta. */}
                    {/* Pílula só de ícones, no padrão pedido. Como ícone sozinho não
                        se explica no toque (não há hover), o nome do modo ATIVO fica
                        escrito ao lado — a pessoa sempre sabe onde está. */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 hidden sm:inline">
                            {MODOS.find(m => m.modo === modo)?.rotulo}
                        </span>
                        <div
                            role="tablist"
                            aria-label="Modo de visualização"
                            className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 p-1 gap-0.5"
                        >
                            {MODOS.map(m => {
                                const ativo = modo === m.modo;
                                return (
                                    <button
                                        key={m.modo}
                                        id={`esteira-tab-${m.modo}`}
                                        role="tab"
                                        aria-selected={ativo}
                                        aria-controls="esteira-painel-modo"
                                        aria-label={`${m.rotulo} — ${m.ajuda}`}
                                        title={`${m.rotulo} — ${m.ajuda}`}
                                        onClick={() => trocarModo(m.modo)}
                                        className={`flex items-center justify-center w-9 h-8 rounded-full transition-all ${
                                            ativo
                                                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                                                : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200'
                                        }`}
                                    >
                                        {m.icone}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Tudo o que está esperando alguém — régua de contato, notificação de mora, advertência,
                    distrato e negativação. O que antes só aparecia no sininho agora tem fila, dono e prazo.
                </p>
            </div>

            {/* Pool de responsáveis da régua e os dois blocos de KPI só fazem
                sentido no modo Lista: Etapas já mostra o volume por fase (com
                barra proporcional) e Tabela é para triar/exportar — repetir os
                mesmos números em cima das duas seria o cabeçalho competindo com
                o próprio conteúdo que ele antecede. Os filtros continuam
                valendo em todos os modos porque todos leem `visiveis`. */}
            {modo === 'lista' ? (
                <>
                    {/* Pool de responsáveis da régua. Só aparece quando a régua está
                        em cena — nas filas de documento ele não tem o que configurar. */}
                    {!filtros.grupo || filtros.grupo === 'regua' || filtros.grupo === 'notificacoes' ? (
                        <ResponsaveisPorEtapaCard
                            mapa={mapaResponsaveis}
                            perfis={perfisElegiveis}
                            salvando={salvandoEtapa}
                            onChange={trocarPoolDaEtapa}
                        />
                    ) : null}

                    {/* KPIs por grupo — a fila em si, o que compõe o volume total. Linha
                        principal, maior peso visual. */}
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                            Por grupo
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                            {GRUPOS.map(g => {
                                const ativo = filtros.grupo === g.grupo;
                                return (
                                    <button
                                        key={g.grupo}
                                        onClick={() => setFiltros(f => ({ ...f, grupo: ativo ? '' : g.grupo, fila: '' }))}
                                        className={`text-left rounded-lg p-4 border-l-4 ${g.accent} transition-all ${
                                            ativo
                                                ? 'bg-gray-100 dark:bg-gray-700 ring-2 ring-primary/40'
                                                : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                            {ICONE_GRUPO[g.grupo]}
                                            <span className="text-xs font-bold">{g.titulo}</span>
                                        </div>
                                        <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                                            {kpis.porGrupo[g.grupo] - (g.grupo === 'negativacao' ? nAcompanhamento : 0)}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{g.descricao}</div>
                                        {g.grupo === 'negativacao' && nAcompanhamento > 0 ? (
                                            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                + {nAcompanhamento} negativados só para acompanhar
                                            </div>
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Sinais de risco e produtividade — menores que a linha de grupo de
                        propósito: respondem "como estamos indo", não "quanto tem para
                        fazer". Ficar do mesmo tamanho competia por atenção com a fila. */}
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                            Sinais
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                            <button
                                aria-pressed={filtros.minDiasParado > 0}
                                onClick={() => setFiltros(f => ({ ...f, minDiasParado: f.minDiasParado > 0 ? 0 : 8 }))}
                                className={`text-left rounded-lg px-3.5 py-2.5 transition-all ${
                                    filtros.minDiasParado > 0
                                        ? 'bg-red-100 dark:bg-red-900/30 ring-2 ring-red-400/50'
                                        : 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
                                }`}
                                title="Demandas paradas há mais de 7 dias"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                                        <AlertTriangle className="w-3.5 h-3.5" />
                                        <span className="text-xs font-bold">Paradas +7d</span>
                                    </div>
                                    <span className="text-lg font-black text-gray-900 dark:text-white">{kpis.paradasMais7}</span>
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    {formatBRL(kpis.valorPendente)} em aberto
                                </div>
                            </button>

                            <button
                                aria-pressed={filtros.apenasSemDono}
                                onClick={() => setFiltros(f => ({ ...f, apenasSemDono: !f.apenasSemDono, responsavelId: '' }))}
                                className={`text-left rounded-lg px-3.5 py-2.5 transition-all ${
                                    filtros.apenasSemDono
                                        ? 'bg-amber-100 dark:bg-amber-900/30 ring-2 ring-amber-400/50'
                                        : 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                                }`}
                                title="Demandas que ninguém assumiu — é daqui que se puxa trabalho"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                                        <UserPlus className="w-3.5 h-3.5" />
                                        <span className="text-xs font-bold">Sem dono</span>
                                    </div>
                                    <span className="text-lg font-black text-gray-900 dark:text-white">{kpis.semDono}</span>
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    clique para filtrar
                                </div>
                            </button>

                            {/* Quem teve contato com retorno marcado sai da lista até a
                                data (#6147). O número fica à vista para ninguém achar
                                que o cliente sumiu. */}
                            <button
                                aria-pressed={filtros.apenasAgendados}
                                // Sai de "Trabalhadas hoje": os dois mexem em quem é agendado e um anulava o outro.
                                onClick={() => setFiltros(f => ({ ...f, apenasAgendados: !f.apenasAgendados, apenasTrabalhadasHoje: false }))}
                                className={`text-left rounded-lg px-3.5 py-2.5 transition-all ${
                                    filtros.apenasAgendados
                                        ? 'bg-sky-100 dark:bg-sky-900/30 ring-2 ring-sky-400/50'
                                        : 'bg-sky-50 dark:bg-sky-900/20 hover:bg-sky-100 dark:hover:bg-sky-900/30'
                                }`}
                                title="Clientes com retorno agendado — saem da lista até a data e voltam sozinhos nela"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 text-sky-600 dark:text-sky-400">
                                        <CalendarClock className="w-3.5 h-3.5" />
                                        <span className="text-xs font-bold">Retorno agendado</span>
                                    </div>
                                    <span className="text-lg font-black text-gray-900 dark:text-white">{agendados}</span>
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    {filtros.apenasAgendados ? 'mostrando só os agendados' : 'fora da lista até a data'}
                                </div>
                            </button>

                            {/* Quem tem distrato é tratado em Cancelamentos, não na
                                rotina de cobrança (pedido da Dhaiane, 07/10/2026).
                                Fica fora da lista e aparece só por aqui. */}
                            <button
                                onClick={() => setFiltros(f => ({ ...f, apenasDistrato: !f.apenasDistrato, grupo: '', fila: '' }))}
                                className={`text-left rounded-lg px-3.5 py-2.5 transition-all ${
                                    filtros.apenasDistrato
                                        ? 'bg-slate-200 dark:bg-slate-700 ring-2 ring-slate-400/50'
                                        : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                                title="Distratos e cobranças de UC com distrato — fora da rotina de cobrança"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                                        <FileSignature className="w-3.5 h-3.5" />
                                        <span className="text-xs font-bold">Com distrato</span>
                                    </div>
                                    <span className="text-lg font-black text-gray-900 dark:text-white">{doDistrato}</span>
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    {filtros.apenasDistrato ? 'mostrando só o distrato' : 'fora da lista de cobrança'}
                                </div>
                            </button>

                            <button
                                type="button"
                                aria-pressed={filtros.apenasTrabalhadasHoje}
                                onClick={() => setFiltros(f => ({ ...f, apenasTrabalhadasHoje: !f.apenasTrabalhadasHoje, apenasSemDono: false, apenasAgendados: false, responsavelId: '', grupo: '', fila: '' }))}
                                className={`text-left rounded-lg px-3.5 py-2.5 transition-colors ${filtros.apenasTrabalhadasHoje
                                    ? 'bg-emerald-100 dark:bg-emerald-900/40 ring-2 ring-emerald-400/60'
                                    : 'bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'}`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        <span className="text-xs font-bold">Trabalhadas hoje</span>
                                    </div>
                                    <span className="text-lg font-black text-gray-900 dark:text-white">{nTrabalhadas}</span>
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    {filtros.apenasTrabalhadasHoje ? 'mostrando só os trabalhados · clique para sair' : 'contato, status, régua e andamento · clique para ver'}
                                </div>
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                /* Etapas e Tabela cortam direto para o conteúdo — só uma linha
                   compacta com os totais que os dois blocos acima mostravam,
                   para não perder de vista "quanto" enquanto se olha "onde"
                   ou "qual". */
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 -mt-2">
                    <span><strong className="text-gray-900 dark:text-white">{base.length - nAcompanhamento}</strong> pendentes</span>
                    <span className="text-red-600 dark:text-red-400">
                        <strong>{kpis.paradasMais7}</strong> paradas +7d
                    </span>
                    <span className="text-amber-600 dark:text-amber-400">
                        <strong>{kpis.semDono}</strong> sem dono
                    </span>
                    <span className="text-sky-600 dark:text-sky-400">
                        <strong>{agendados}</strong> com retorno agendado
                    </span>
                    {/* Em Etapas e Tabela o filtro também precisa estar à vista, senão a lista
                        some sem pista do porquê. */}
                    <button
                        type="button"
                        aria-pressed={filtros.apenasTrabalhadasHoje}
                        onClick={() => setFiltros(f => ({ ...f, apenasTrabalhadasHoje: !f.apenasTrabalhadasHoje, apenasSemDono: false, apenasAgendados: false, responsavelId: '', grupo: '', fila: '' }))}
                        className={`hover:underline ${filtros.apenasTrabalhadasHoje ? 'font-bold text-emerald-700 dark:text-emerald-300' : 'text-emerald-600 dark:text-emerald-400'}`}
                    >
                        <strong>{nTrabalhadas}</strong> {filtros.apenasTrabalhadasHoje ? 'trabalhadas hoje (mostrando)' : 'trabalhadas hoje'}
                    </button>
                    <button
                        aria-pressed={filtros.apenasDistrato}
                        onClick={() => setFiltros(f => ({ ...f, apenasDistrato: !f.apenasDistrato, grupo: '', fila: '' }))}
                        className={`hover:underline ${filtros.apenasDistrato ? 'font-bold text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}
                    >
                        <strong>{doDistrato}</strong> {filtros.apenasDistrato ? 'com distrato (mostrando)' : 'com distrato (fora da lista)'}
                    </button>
                    <span>{formatBRL(kpis.valorPendente)} em aberto</span>
                </div>
            )}

            {/* Bloqueio interno — quem da Vigor está segurando documento.
                Aparece só quando existe: é chamada para ação, não enfeite fixo.
                Foi o achado do levantamento de 11/09/2026 (52 dos 87 distratos
                travados dentro de casa, invisíveis porque a assinatura só
                existia no Autentique). */}
            {bloqueadores.length && modo !== 'resumo' ? (
                <div className="rounded-lg border border-orange-200 dark:border-orange-900/40 bg-orange-50/60 dark:bg-orange-900/10 p-4">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2.5">
                        <FileSignature className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                            Travado esperando a Vigor assinar
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            — não depende do cliente, resolve internamente
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {bloqueadores.map(b => (
                            <a
                                key={b.email}
                                href={`mailto:${b.email}?subject=${encodeURIComponent(`${b.demandas} documento(s) aguardando sua assinatura`)}`}
                                // Não impede o mailto: o e-mail abre e, em paralelo,
                                // a cobrança fica registrada em cada documento.
                                onClick={() => registrarCobranca(
                                    demandasTravadasPor(todas, b.email),
                                    `Cobrança de assinatura enviada por e-mail para ${b.email}.`
                                )}
                                title={`Cobrar ${b.nome} por e-mail — ${b.email}. A cobrança fica registrada nos ${b.demandas} documento(s).`}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-900/40 hover:border-orange-400 dark:hover:border-orange-700 transition-colors"
                            >
                                <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">{b.nome}</span>
                                <span className="text-xs font-black text-orange-700 dark:text-orange-400">{b.demandas}</span>
                            </a>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* Filtros */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                {user ? (
                    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                        <button
                            onClick={() => setFiltros(f => ({ ...f, responsavelId: user.id }))}
                            className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                                vendoAsMinhas
                                    ? 'bg-primary text-white'
                                    : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                            Meus itens{minhasPendentes ? ` (${minhasPendentes})` : ''}
                        </button>
                        <button
                            onClick={() => setFiltros(f => ({ ...f, responsavelId: '' }))}
                            className={`px-3 py-1.5 text-xs font-bold border-l border-gray-200 dark:border-gray-600 transition-colors ${
                                !filtros.responsavelId
                                    ? 'bg-primary text-white'
                                    : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                            Todos
                        </button>
                    </div>
                ) : null}

                {/* Busca sozinha na primeira linha — é a ação mais usada e a que
                    mais precisa de espaço para o texto digitado. Os seletores vêm
                    agrupados por o que filtram: fila/idade primeiro (onde está),
                    depois quem/quem-com (dono). */}
                <div className="relative">
                    <label htmlFor="esteira-busca" className="sr-only">Buscar por cliente, CPF/CNPJ ou UC</label>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        id="esteira-busca"
                        type="text"
                        placeholder="Buscar por cliente, CPF/CNPJ ou UC..."
                        value={filtros.search}
                        onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
                        className={`w-full pl-9 pr-3 ${inputCls}`}
                    />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <select
                        value={filtros.fila}
                        onChange={e => trocarFila(e.target.value as EsteiraFila | '')}
                        className={inputCls}
                    >
                        <option value="">Todas as filas</option>
                        {ORDEM_FILAS
                            .filter(f => !filtros.grupo || FILAS[f].grupo === filtros.grupo)
                            .map(f => (
                                <option key={f} value={f}>
                                    {FILAS[f].titulo} ({kpis.porFila[f]})
                                </option>
                            ))}
                    </select>

                    <select
                        value={filtros.minDiasParado}
                        onChange={e => setFiltros(f => ({ ...f, minDiasParado: Number(e.target.value) }))}
                        className={inputCls}
                    >
                        <option value={0}>Qualquer idade</option>
                        <option value={4}>Parado há 3+ dias</option>
                        <option value={8}>Parado há 7+ dias</option>
                        <option value={16}>Parado há 15+ dias</option>
                        <option value={31}>Parado há 30+ dias</option>
                    </select>

                    <select
                        value={filtros.ordem}
                        onChange={e => setFiltros(f => ({ ...f, ordem: e.target.value as Filtros['ordem'] }))}
                        className={inputCls}
                        aria-label="Ordem da lista"
                        title="Quem ficou inadimplente há menos tempo é quem mais paga quando a gente liga"
                    >
                        <option value="recente">Atraso mais recente primeiro</option>
                        <option value="antigo">Mais parado primeiro</option>
                    </select>

                    <select
                        value={filtros.status}
                        onChange={e => setFiltros(f => ({ ...f, status: e.target.value as CobrancaAcaoStatus | '' }))}
                        className={inputCls}
                        title="Só a régua de contato humano tem status próprio — as demais filas são sempre pendências"
                    >
                        <option value="pendente">Pendentes</option>
                        <option value="concluido">Concluídos (régua)</option>
                        <option value="dispensado">Dispensados (régua)</option>
                        <option value="">Todos</option>
                    </select>

                    <span className="w-px h-6 bg-gray-200 dark:bg-gray-600 hidden sm:block" />

                    <select
                        value={filtros.statusNegociacao}
                        onChange={e => setFiltros(f => ({ ...f, statusNegociacao: e.target.value as CobrancaStatusNegociacao | '' }))}
                        className={inputCls}
                        title="Status da negociação com o cliente"
                    >
                        <option value="">Toda negociação</option>
                        {(Object.keys(STATUS_NEGOCIACAO_META) as CobrancaStatusNegociacao[]).map(s => (
                            <option key={s} value={s}>{STATUS_NEGOCIACAO_META[s].label}</option>
                        ))}
                    </select>

                    <select
                        value={filtros.responsavelId}
                        onChange={e => setFiltros(f => ({ ...f, responsavelId: e.target.value }))}
                        className={inputCls}
                    >
                        <option value="">Todos os responsáveis</option>
                        {perfisElegiveis.map(p => (
                            <option key={String(p.id)} value={String(p.id)}>{p.full_name}</option>
                        ))}
                    </select>

                    {temFiltroExtra ? (
                        <button onClick={limparFiltros} className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-red-500 flex items-center gap-1">
                            <Filter className="w-4 h-4" /> Limpar
                        </button>
                    ) : null}
                </div>

                {!loading ? (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            Mostrando {paginadas.length} de {visiveis.length} demandas
                            {visiveis.length !== base.length ? ` (${base.length} no total)` : ''}.
                        </p>
                        {/* Horário da última atualização: com a tela se atualizando
                            sozinha, a pessoa precisa saber se o que vê é de agora. */}
                        <button
                            onClick={() => carregar(true)}
                            disabled={atualizando}
                            aria-live="polite"
                            className="text-xs text-gray-400 dark:text-gray-500 hover:text-primary flex items-center gap-1.5 disabled:cursor-default"
                            title="Atualiza sozinho a cada 5 minutos e ao voltar para a aba"
                        >
                            <RotateCcw size={12} className={atualizando ? 'animate-spin' : ''} />
                            {atualizando
                                ? 'Atualizando…'
                                : atualizadoEm
                                    ? `Atualizado às ${atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                                    : 'Atualizar'}
                        </button>
                    </div>
                ) : null}
            </div>

            {/* Conteúdo, conforme o modo. Os três recebem `visiveis` — a mesma
                fila já filtrada; o modo muda a leitura, nunca o recorte. */}
            <div
                id="esteira-painel-modo"
                role="tabpanel"
                aria-labelledby={`esteira-tab-${modo}`}
                tabIndex={-1}
            >
            {loading ? (
                <div className="p-10 text-center text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Carregando o painel...
                </div>
            ) : modo === 'etapas' ? (
                <QuadroEtapas
                    demandas={visiveis}
                    onAbrirFila={fila => {
                        trocarFila(fila);
                        trocarModo('lista');
                        addToast(`Filtro trocado para a fila "${FILAS[fila].titulo}" — os demais filtros continuam valendo.`, 'info');
                    }}
                />
            ) : modo === 'calendario' ? (
                <CalendarioDemandas demandas={visiveis} onAbrirDetalhe={abrirDetalhe} />
            ) : modo === 'resumo' ? (
                <ResumoGerencial
                    demandas={visiveis}
                    nomePorEmail={nomePorEmail}
                    // Clicar numa pessoa leva para a Lista só com os itens dela —
                    // o Resumo aponta o problema, a Lista é onde se resolve.
                    onVerPessoa={id => {
                        setFiltros(f => id
                            ? { ...f, responsavelId: id, apenasSemDono: false }
                            : { ...f, responsavelId: '', apenasSemDono: true });
                        trocarModo('lista');
                    }}
                />
            ) : modo === 'tabela' ? (
                <TabelaDemandas
                    demandas={visiveis}
                    onAbrirDetalhe={abrirDetalhe}
                    // Abrir uma linha leva para a Lista focada naquele cliente,
                    // que é onde estão as ações — a tabela é para triar, não para
                    // trabalhar. Isto sobrescreve a busca que a pessoa tinha
                    // digitado, então avisa em vez de trocar em silêncio — e só
                    // sobrescreve de fato se havia algo diferente para perder.
                    onAbrir={d => {
                        const novaBusca = d.cpf_cnpj_cliente || d.cliente_nome || '';
                        setFiltros(f => {
                            if (f.search && f.search !== novaBusca) {
                                addToast(`Busca trocada de "${f.search}" para "${novaBusca}" — limpe a busca para voltar ao que você tinha.`, 'info');
                            }
                            return { ...f, search: novaBusca };
                        });
                        trocarModo('lista');
                    }}
                />
            ) : visiveis.length === 0 ? (
                <div className="p-12 text-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm font-bold text-gray-700 dark:text-gray-200">
                        {filtros.apenasTrabalhadasHoje && diaFiltrado.length > 0
                            ? 'Os trabalhados de hoje já não têm pendência no painel'
                            : temFiltroExtra ? 'Nada por aqui com este recorte' : 'O painel está limpo'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {filtros.apenasTrabalhadasHoje && diaFiltrado.length > 0
                            ? 'Nenhum card pendente neste recorte — quem foi atendido está logo abaixo.'
                            : temFiltroExtra
                            ? 'Tente outra fila, ajuste a idade mínima ou limpe os filtros.'
                            : 'Nenhuma demanda pendente no momento.'}
                    </p>
                    {filtros.apenasTrabalhadasHoje ? (
                        <div className="mt-4 max-w-xl mx-auto text-left">
                            <TrabalhadosSemCard dia={diaFiltrado} comCard={visiveis.map(cpfDe)} onAbrir={c => setClienteAberto({ cpf_cnpj_cliente: c.cpf, cliente_nome: c.nome } as DemandaEsteira)} />
                        </div>
                    ) : null}
                    {filtros.search.trim().length >= 3 ? (
                        <ProcurarForaDoPainel termo={filtros.search} onAbrir={c => setClienteAberto({ cpf_cnpj_cliente: c.cpf, cliente_nome: c.nome } as DemandaEsteira)} />
                    ) : null}
                    {temFiltroExtra ? (
                        <button
                            onClick={limparFiltros}
                            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-primary dark:text-primary-light border border-primary/25 dark:border-primary-light/40 hover:bg-primary/10 transition-colors"
                        >
                            <X size={13} /> Limpar filtros
                        </button>
                    ) : null}
                </div>
            ) : (
                <>
                    {filtros.apenasTrabalhadasHoje ? (
                        <TrabalhadosSemCard dia={diaFiltrado} comCard={visiveis.map(cpfDe)} onAbrir={c => setClienteAberto({ cpf_cnpj_cliente: c.cpf, cliente_nome: c.nome } as DemandaEsteira)} />
                    ) : null}
                    <div ref={listaRef} className="space-y-3">
                        {paginadas.map(d => (
                            <CardDemanda
                                key={d.id}
                                demanda={d}
                                usuarioId={user?.id}
                                compartilhado={!!quemVe && compartilhadaCom(d, quemVe, poolContato)}
                                paraQuem={nomeDeQuemVe}
                                emAcao={demandasEmAcao.has(d.id)}
                                onRegistrar={modo => setModal({ demanda: d, modo })}
                                onRetirarNegativacao={() => registrarRetirada(d)}
                                onReabrir={reabrir}
                                onGerenciar={() => setClienteAberto(d)}
                                onAssumir={() => assumir(d)}
                                onLiberar={() => liberar(d)}
                                onAndamento={() => setAndamentoAberto({ demanda: d, modo: 'registrar' })}
                                podeCorrigirAnotacao={podeCorrigirAndamento(d.atribuicao, user?.id, profile?.role)}
                                onCorrigirAnotacao={() => setAndamentoAberto({ demanda: d, modo: 'corrigir' })}
                                onExcluirAnotacao={() => excluirAnotacao(d)}
                                onAcaoDoc={acao => setAcaoDoc({ demanda: d, acao })}
                                onLembrarAssinatura={() => lembrarAssinatura(d)}
                                onEmitirMora={() => setEmitindo('mora')}
                                onAbrirDetalhe={() => abrirDetalhe(d)}
                                carregandoDetalhe={buscandoUc === d.id}
                                onRegistrarCobranca={texto => registrarCobranca([d], texto)}
                                naRota={paraRota.has(d.id)}
                                onAlternarRota={() => alternarNaRota(d.id)}
                            />
                        ))}
                    </div>

                    {qtdVisivel < visiveis.length ? (
                        <div className="flex justify-center pt-2">
                            <button
                                onClick={() => setQtdVisivel(q => q + TAMANHO_PAGINA)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <ChevronDown size={15} />
                                Carregar mais {Math.min(TAMANHO_PAGINA, visiveis.length - qtdVisivel)}
                                <span className="text-gray-400 dark:text-gray-500 font-normal">
                                    ({visiveis.length - qtdVisivel} restantes)
                                </span>
                            </button>
                        </div>
                    ) : null}

                    {/* Cliente que já pagou não tem card: com resultados na tela o botão
                        só existia no estado vazio e ninguém o achava (#6211). */}
                    {filtros.search.trim().length >= 3 ? (
                        <div className="pt-2 text-center">
                            <ProcurarForaDoPainel termo={filtros.search} onAbrir={c => setClienteAberto({ cpf_cnpj_cliente: c.cpf, cliente_nome: c.nome } as DemandaEsteira)} />
                        </div>
                    ) : null}

                    {/* Barra da seleção para a rota. No FIM da lista e sticky no rodapé:
                        no começo ela sairia de vista assim que a pessoa rolasse. */}
                    {paraRota.size > 0 ? (
                        <div className="sticky bottom-4 z-20">
                            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#1d220f] text-white shadow-2xl">
                                <div className="text-xs">
                                    <span className="font-black tabular-nums">{paraRota.size}</span> cliente{paraRota.size > 1 ? 's' : ''} ·{' '}
                                    <span className="font-bold text-[#F6A937] tabular-nums">{ucsDaRota.length}</span> unidade{ucsDaRota.length === 1 ? '' : 's'} para visitar
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setParaRota(new Set())}
                                        className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold transition-colors"
                                    >
                                        <X size={14} /> Limpar
                                    </button>
                                    <button
                                        onClick={gerarRotaDaSelecao}
                                        disabled={ucsDaRota.length === 0}
                                        title={ucsDaRota.length === 0 ? 'Os clientes marcados não têm UC para visitar' : 'Abre a montagem da visita com estas UCs já marcadas'}
                                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-xs font-bold transition-colors disabled:opacity-50"
                                    >
                                        <RouteIcon size={14} /> Enviar para agendamento de visita / tratativa presencial
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </>
            )}
            </div>

            <RegistrarAcaoModal
                alvo={modal ? alvoDoRegistro(modal.demanda, modal.modo) : null}
                modo={modal?.modo ?? 'contato'}
                onClose={() => setModal(null)}
                onConfirmarRegistro={confirmarRegistro}
                onConfirmarDispensa={confirmarDispensa}
            />

            <ClienteCobrancaModal
                isOpen={!!clienteAberto}
                onClose={() => { setClienteAberto(null); carregar(true); }}
                cpfCnpjCliente={clienteAberto?.cpf_cnpj_cliente ?? null}
                clienteNome={clienteAberto?.cliente_nome ?? null}
            />

            <AndamentoModal
                demanda={andamentoAberto?.demanda ?? null}
                modo={andamentoAberto?.modo}
                usuarioId={user?.id}
                onClose={() => setAndamentoAberto(null)}
                onConfirmar={confirmarAndamento}
            />

            <AcaoDocumentoModal
                demanda={acaoDoc?.demanda ?? null}
                acao={acaoDoc?.acao ?? 'registrar_envio'}
                onClose={() => setAcaoDoc(null)}
                onConfirmar={executarAcaoDoc}
            />

            {/* Emissão. Os três já existiam na tela de UCs e são montados aqui
                sem `unidade`: cada um tem busca de UC própria, então a pessoa
                escolhe o cliente dentro do modal. Fechar recarrega o painel para
                o documento recém-criado já aparecer na fila certa. */}
            <NotificacaoMoraModal
                isOpen={emitindo === 'mora'}
                onClose={() => { setEmitindo(null); carregar(true); }}
            />
            <ComunicadoAdvertenciaModal
                isOpen={emitindo === 'advertencia'}
                onClose={() => { setEmitindo(null); carregar(true); }}
            />
            <CancelUCModal
                isOpen={emitindo === 'distrato'}
                onClose={() => { setEmitindo(null); carregar(true); }}
            />

            {/* Detalhe da UC, aberto pelo clique no nome nas filas de documento. */}
            <UCManagementModal
                isOpen={!!ucAberta}
                onClose={() => setUcAberta(null)}
                unidade={ucAberta}
                onSave={() => { setUcAberta(null); carregar(true); }}
            />
        </div>
    );
};

/** Faturas vencidas em aberto do cliente (#6211): até 3 à vista, o resto em "ver todas". */
const FaturasAbertas: React.FC<{ faturas: NonNullable<DemandaEsteira['faturas_abertas']> }> = ({ faturas }) => {
    const [todas, setTodas] = useState(false);
    const hoje = new Date();
    const lista = todas ? faturas : faturas.slice(0, 3);
    const total = faturas.reduce((s, f) => s + Number(f.valor ?? 0), 0);
    return (
        <div className="mt-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                Débitos em aberto · {faturas.length} fatura{faturas.length === 1 ? '' : 's'} · {formatBRL(total)}
            </p>
            <ul className="flex flex-wrap gap-1.5">
                {lista.map(f => {
                    const dias = f.vencimento ? Math.max(0, Math.floor((hoje.getTime() - Date.parse(`${f.vencimento}T12:00:00`)) / 86_400_000)) : null;
                    return (
                        <li key={f.fatura_id}
                            className="text-[11px] font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded"
                            title={`Fatura ${f.fatura_id}${f.vencimento ? ` · venc. ${formatDia(f.vencimento)}` : ''}`}>
                            UC {f.uc_codigo ?? '—'} · {f.mes_referencia ?? '—'} · {f.valor != null ? formatBRL(f.valor) : '—'}{dias != null ? ` · vencida há ${dias}d` : ''}{f.distrato ? ' · distrato' : ''}
                        </li>
                    );
                })}
            </ul>
            {faturas.length > 3 ? (
                <button type="button" aria-expanded={todas} onClick={() => setTodas(v => !v)} className="mt-1 text-[11px] font-semibold text-primary dark:text-primary-light hover:underline">
                    {todas ? 'mostrar menos' : `ver todas (${faturas.length})`}
                </button>
            ) : null}
        </div>
    );
};

/** O serviço grava o status cru ("status: negociando"); na tela vai o rótulo da negociação. */
function rotuloAcaoTrabalho(acao: string): string {
    const m = /^status: (\w+)$/.exec(acao);
    const meta = m ? (STATUS_NEGOCIACAO_META as Record<string, { label: string }>)[m[1]] : null;
    return meta ? `mudou para ${meta.label}` : acao;
}

/** Trabalhados hoje que não têm card no painel (#6211): pagaram ou ficaram com retorno marcado. */
const TrabalhadosSemCard: React.FC<{
    dia: Array<{ cpf: string; nome: string | null; eventos: Array<{ por: string | null; acao: string; em: string }> }>;
    comCard: string[];
    onAbrir: (c: { cpf: string; nome: string | null }) => void;
}> = ({ dia, comCard, onAbrir }) => {
    const ja = new Set(comCard);
    const fora = dia.filter(t => !ja.has(t.cpf));
    if (!fora.length) return null;
    return (
        <section aria-label="Também trabalhados hoje, sem pendência no painel" className="mb-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/10 p-3">
            <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300 mb-2">
                Também trabalhados hoje ({fora.length}) — sem pendência no painel agora
            </p>
            <ul className="space-y-1">
                {fora.map(t => (
                    <li key={t.cpf}>
                        <button type="button" onClick={() => onAbrir(t)}
                            className="w-full text-left rounded-md px-2 py-1.5 hover:bg-white dark:hover:bg-gray-800 transition-colors">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{t.nome || 'Cliente sem nome'}</span>
                            <span className="block text-xs text-gray-600 dark:text-gray-400">
                                {t.eventos.map(e => `${(e.por ?? 'alguém').split(' ')[0]}: ${rotuloAcaoTrabalho(e.acao)}`).join(' · ')}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </section>
    );
};

/** Busca fora do painel (#6211): cliente que pagou e não tem mais card. */
const ProcurarForaDoPainel: React.FC<{ termo: string; onAbrir: (c: { cpf: string; nome: string | null }) => void }> = ({ termo, onAbrir }) => {
    const [estado, setEstado] = useState<'ocioso' | 'buscando' | 'pronto' | 'erro'>('ocioso');
    const [achados, setAchados] = useState<Array<{ cpf: string; nome: string | null; ucs: string[] }>>([]);
    useEffect(() => { setEstado('ocioso'); setAchados([]); }, [termo]);
    const procurar = async () => {
        setEstado('buscando');
        try { setAchados(await procurarClientesCobranca(termo)); setEstado('pronto'); }
        catch { setEstado('erro'); }
    };
    return (
        // aria-live: o resultado aparece no lugar do botão e o leitor de tela não percebia.
        <div className="mt-4 max-w-md mx-auto text-left" aria-live="polite">
            {estado === 'ocioso' ? (
                <button type="button" onClick={procurar}
                    className="mx-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-primary dark:text-primary-light border border-primary/25 dark:border-primary-light/40 hover:bg-primary/10 transition-colors">
                    <Search size={13} /> Procurar "{termo}" em todos os clientes
                </button>
            ) : estado === 'buscando' ? (
                <p className="text-center text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Procurando…</p>
            ) : estado === 'erro' ? (
                <p className="text-center text-xs text-red-600 dark:text-red-400">Não foi possível procurar agora.</p>
            ) : achados.length === 0 ? (
                <p className="text-center text-xs text-gray-500 dark:text-gray-400">Nenhum cliente encontrado com "{termo}".</p>
            ) : (
                <div className="space-y-1.5">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">Fora do painel — abra para ver a cobrança ou registrar o pagamento. Abrir cria o acompanhamento do cliente, sem responsável, se ele ainda não tiver:</p>
                    {achados.map(c => (
                        <button key={c.cpf} type="button" onClick={() => onAbrir(c)}
                            className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                            <span className="block text-sm font-semibold text-gray-900 dark:text-white">{c.nome || 'Cliente sem nome'}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">CPF/CNPJ {c.cpf}{c.ucs.length ? ` · UC ${c.ucs.slice(0, 3).join(', ')}` : ''}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/** Cobrança do cliente (régua, negociação, negativação) — o dono é da carteira, não da demanda. */
function ehDoCliente(d: DemandaEsteira): boolean {
    return d.origem === 'cobranca_acao_manual' || d.origem === 'cobranca_clientes';
}

const ETAPAS_DO_MODO: Record<ModoRegistro, CobrancaAcaoEtapa[] | null> = {
    contato: ['D+4', 'D+10'],
    conferencia: ['D+24'],
    dispensar: null,
};

const ORDEM_ETAPA: CobrancaAcaoEtapa[] = ['D+4', 'D+10', 'D+17', 'D+24'];

/** O que o modal de registro mostra sobre o card. */
function alvoDoRegistro(d: DemandaEsteira, modo: ModoRegistro): AlvoRegistro {
    const filtro = ETAPAS_DO_MODO[modo];
    const etapas = pendenciasDaRegua(d).filter(a => !filtro || filtro.includes(a.etapa));
    return {
        cliente: d.cliente_nome || 'Cliente sem nome',
        etapas: [...new Set(etapas.map(a => a.etapa))].sort((a, b) => ORDEM_ETAPA.indexOf(a) - ORDEM_ETAPA.indexOf(b)),
        valor: d.valor,
        vencimento: etapas.map(a => a.data_referencia).sort()[0] ?? null,
        proximoAtual: d.proximo_contato_em ?? null,
        semCpf: !d.cpf_cnpj_cliente,
    };
}

interface CardProps {
    demanda: DemandaEsteira;
    usuarioId?: string;
    /**
     * Não é da pessoa, mas aparece para ela: retorno de colega do contato ou
     * cliente sem dono (ver `compartilhadaCom`).
     */
    compartilhado?: boolean;
    /** Primeiro nome de quem está sendo filtrado, quando não é quem está logado. */
    paraQuem?: string | null;
    /** Já disparou "assumir"/"devolver" e a esteira ainda não recarregou. */
    emAcao?: boolean;
    onRegistrar: (modo: ModoRegistro) => void;
    onRetirarNegativacao: () => void;
    onReabrir: (acao: CobrancaAcaoManual) => void;
    onGerenciar: () => void;
    onAssumir: () => void;
    onLiberar: () => void;
    onAndamento: () => void;
    /** Quem anotou, ou adm/gerente — ver `podeCorrigirAndamento`. */
    podeCorrigirAnotacao?: boolean;
    onCorrigirAnotacao?: () => void;
    onExcluirAnotacao?: () => void;
    onAcaoDoc: (acao: AcaoDocumento) => void;
    onEmitirMora: () => void;
    onAbrirDetalhe: () => void;
    carregandoDetalhe: boolean;
    onRegistrarCobranca: (texto: string) => void;
    onLembrarAssinatura: () => void;
    /** Marcado para entrar na rota de cobrança. */
    naRota?: boolean;
    onAlternarRota?: () => void;
}

export const CardDemanda: React.FC<CardProps> = ({
    demanda: d, usuarioId, compartilhado = false, paraQuem = null, emAcao, onRegistrar, onRetirarNegativacao, onReabrir, onGerenciar,
    onAssumir, onLiberar, onAndamento, podeCorrigirAnotacao = false, onCorrigirAnotacao, onExcluirAnotacao,
    onAcaoDoc, onLembrarAssinatura, onEmitirMora,
    onAbrirDetalhe, carregandoDetalhe, onRegistrarCobranca, naRota = false, onAlternarRota,
}) => {
    const meta = FILAS[d.fila];
    const pendente = d.status === 'pendente';
    const ehRegua = ehDaRegua(d);
    const doCliente = ehDoCliente(d);
    const itens = d.itens ?? [d];
    // Linha da régua quando o card é uma etapa só (concluída/dispensada, no filtro).
    const acao = ehRegua && !d.itens ? (d.bruto as CobrancaAcaoManual) : null;
    const negociacao = d.status_negociacao ? STATUS_NEGOCIACAO_META[d.status_negociacao] : null;

    // Etapas da régua que este card resolve — pode ser mais de uma: o card
    // junta tudo o que é do mesmo cliente com a mesma pessoa.
    const pendentesRegua = pendente ? pendenciasDaRegua(d) : [];
    const etapasPendentes = new Set(pendentesRegua.map(a => a.etapa));
    const filasDoCard = new Set(itens.map(i => i.fila));
    // Faturas sem repetir: no card agrupado, `bruto.faturas` já é a união.
    const faturas = (pendentesRegua.length || acao)
        ? ((d.bruto as CobrancaAcaoManual | undefined)?.faturas ?? [])
        : [];

    // Só a régua (D+4/10/17/24) traz as faturas com o código da UC na Equatorial.
    // Nas filas de documento o "UC" do card pode ser o id interno (o distrato
    // mostra "UC 11"), que não casa com nada na Central — por isso a caixinha
    // aparece só onde há fatura. Ver `ucsDasDemandas`.
    const temUcParaRota = !!onAlternarRota && ucsDasDemandas([d]).length > 0;

    // Herdado da ManuaisTab: se este CPF/CNPJ já foi notificado em qualquer UC,
    // oferecer "Gerar notificação" de novo é errado — o caminho é registrar o
    // desfecho na negociação.
    const acaoD17 = pendentesRegua.find(a => a.etapa === 'D+17') ?? (acao?.etapa === 'D+17' ? acao : null);
    const estadoNotif = acaoD17
        ? derivarEstadoNotificacaoD17(acaoD17)
        : ({ tipo: 'normal' } as const);
    const jaNotificada = estadoNotif.tipo === 'ja_notificada';

    const temContato = etapasPendentes.has('D+4') || etapasPendentes.has('D+10')
        || filasDoCard.has('followup_vencido') || filasDoCard.has('sem_retorno')
        || filasDoCard.has('sem_contato') || filasDoCard.has('sem_responsavel');
    // Encaminhado pelo "Gerenciar Cobrança" é o mesmo trabalho do D+17.
    const temD17 = etapasPendentes.has('D+17') || (pendente && filasDoCard.has('notificacao_extrajudicial'));
    const temD24 = etapasPendentes.has('D+24');
    const temRetirada = pendente && filasDoCard.has('negativacao_retirar');

    // Assumir: cobrança do cliente vai para a carteira (precisa de CPF);
    // documento usa a atribuição da esteira, com devolver e andamento.
    const dono = donoEfetivo(d);
    const meu = !!usuarioId && dono === usuarioId;
    const minhaEtapa = !!usuarioId && !!d.donos_etapas?.some(o => o.id === usuarioId);
    // Dono só pelo último contato (sem carteira) também pode assumir, inclusive quem registrou o contato: é o único jeito de pôr o cliente na carteira.
    const donoDaCarteira = !!dono && !d.dono_pelo_contato;
    // Card com ação recente que ainda assim está em "Paradas +7d" (dívida antiga sem retorno): o chip "aberta há" explica.
    const dividaAntigaParada = d.dias_parado <= 7 && estaParado(d);
    const podeAssumirCliente = doCliente && pendente && !!d.cpf_cnpj_cliente && (!meu || !!d.dono_pelo_contato);
    const nomeDoDono = d.responsavel_nome
        ?? (d.origem === 'cobranca_clientes' ? d.atribuicao?.responsavel_nome : null);
    const podeAtribuirDocumento = !doCliente && pendente;

    // A cor do tempo sobe com o abandono: o que passou de duas semanas parado
    // não pode ter o mesmo peso visual de algo de ontem.
    const corIdade = d.dias_parado > 15
        ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
        : d.dias_parado > 7
            ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

    return (
        <div data-demanda-id={d.id} className={`bg-white dark:bg-gray-800 rounded-lg border border-l-4 ${meta.accent} p-4 transition-colors ${
            naRota ? 'border-[#5F6C37] ring-1 ring-[#5F6C37]/30' : 'border-gray-200 dark:border-gray-700'}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        {temUcParaRota ? (
                            <label className="inline-flex items-center gap-1.5 cursor-pointer" title="Enviar para visita ou tratativa presencial (todas as UCs deste cliente)">
                                <input
                                    type="checkbox"
                                    checked={naRota}
                                    onChange={onAlternarRota}
                                    className="w-4 h-4 rounded border-gray-300 text-[#5F6C37] focus:ring-[#5F6C37] cursor-pointer"
                                />
                                <RouteIcon size={12} className={naRota ? 'text-[#5F6C37]' : 'text-gray-300 dark:text-gray-600'} />
                            </label>
                        ) : null}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${meta.badge}`}>
                            {ICONE_FILA[d.fila]} {meta.titulo}
                        </span>
                        {/* As outras pendências que este card também resolve. */}
                        {(d.outras_filas ?? []).map(f => (
                            <span key={f} className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold opacity-80 ${FILAS[f].badge}`} title={FILAS[f].titulo}>
                                + {FILAS[f].titulo.split(' · ')[0]}
                            </span>
                        ))}
                        {d.trabalho_hoje?.length ? (
                            <span className="max-w-full truncate px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                                title={d.trabalho_hoje.map(t => `${t.por ?? 'alguém'}: ${rotuloAcaoTrabalho(t.acao)}`).join('\n')}>
                                trabalhado hoje · {[...new Set(d.trabalho_hoje.map(t => (t.por ?? 'alguém').split(' ')[0]))].join(', ')}
                            </span>
                        ) : null}
                        {d.acompanhamento ? (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {d.dias_parado <= 0 ? 'negativado hoje' : `negativado há ${d.dias_parado}d`}
                            </span>
                        ) : (
                            <>
                                {/* "mexido hoje" repete o chip "trabalhado hoje" — só um dos dois. */}
                                {d.dias_parado <= 0 && d.trabalho_hoje?.length ? null : <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${corIdade}`}
                                    title={d.ultima_acao_em ? `Parado desde a última ação, em ${formatDia(d.ultima_acao_em)}` : 'Parado desde que a pendência começou — ninguém agiu ainda'}>
                                    {/* "mexido hoje" só quando houve ação; sem ação, a pendência é de hoje. */}
                                    {d.dias_parado <= 0 ? (d.ultima_acao_em ? 'mexido hoje' : 'hoje') : `${d.dias_parado}d parado`}
                                </span>}
                                {d.dias_atraso != null && d.dias_atraso > d.dias_parado ? (
                                    <span className={`px-2 py-0.5 rounded-full text-[11px] ring-1 ring-inset ${dividaAntigaParada
                                        ? 'font-bold text-amber-700 dark:text-amber-300 ring-amber-400 dark:ring-amber-600'
                                        : 'font-medium text-gray-500 dark:text-gray-400 ring-gray-300 dark:ring-gray-600'}`}
                                        title={dividaAntigaParada
                                            ? 'Há quanto tempo a pendência existe. Tem contato recente, mas passa de 30 dias sem retorno marcado: por isso conta em "Paradas +7d"'
                                            : 'Há quanto tempo a pendência existe (desde o vencimento, a data marcada ou o documento)'}>
                                        aberta há {d.dias_atraso}d
                                        <span className="sr-only">, mas com ação registrada há {d.dias_parado <= 0 ? 'menos de um dia' : `${d.dias_parado} dia${d.dias_parado === 1 ? '' : 's'}`}{dividaAntigaParada ? '. Conta como parada: mais de 30 dias sem retorno marcado' : ''}</span>
                                    </span>
                                ) : null}
                            </>
                        )}
                        {/* No SPC e numa fila de trabalho (negociando, retorno…): sem o
                            selo a pessoa negociava sem saber que o nome está sujo (#6209). */}
                        {d.negativado_spc_em && !d.acompanhamento ? (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900"
                                title="Negativado no SPC e ainda sem retirada">
                                no SPC desde {formatDia(d.negativado_spc_em)}
                            </span>
                        ) : null}
                        {negociacao ? (
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${negociacao.classes}`}>
                                {negociacao.label}
                            </span>
                        ) : null}
                        {compartilhado ? (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">
                                {dono
                                    ? `retorno de ${nomeDoDono?.split(' ')[0] ?? 'colega'} · ${paraQuem ? `${paraQuem} também pode atender` : 'você também pode atender'}`
                                    : `sem dono · disponível para ${paraQuem ?? 'você'}`}
                            </span>
                        ) : null}
                        {d.agendado && d.proximo_contato_em ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300">
                                <CalendarClock size={11} /> volta em {formatDia(d.proximo_contato_em)}
                            </span>
                        ) : null}
                        {!pendente ? (
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_ACAO_META[d.status].classes}`}>
                                {STATUS_ACAO_META[d.status].label}
                            </span>
                        ) : null}
                    </div>

                    {/* O nome é a porta para o detalhe: UC nas filas de documento,
                        negociação nas demais. Antes era texto morto e a pessoa
                        tinha de procurar o cliente em outra tela para se situar. */}
                    <button
                        onClick={onAbrirDetalhe}
                        disabled={carregandoDetalhe}
                        className="font-bold text-gray-900 dark:text-white truncate max-w-full text-left hover:text-primary hover:underline underline-offset-2 transition-colors disabled:opacity-60 flex items-center gap-1.5"
                        title={ehDeDocumento(d) && d.uc_numero
                            ? `Abrir a UC ${d.uc_numero} — cadastro, faturas e histórico de documentos`
                            : 'Abrir a negociação de cobrança deste cliente'}
                    >
                        <span className="truncate">{d.cliente_nome || 'Cliente sem nome'}</span>
                        {carregandoDetalhe
                            ? <Loader2 size={13} className="animate-spin shrink-0" />
                            : <ExternalLink size={12} className="shrink-0 opacity-0 group-hover/card:opacity-60 transition-opacity" />}
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {d.cpf_cnpj_cliente ? `CPF/CNPJ ${d.cpf_cnpj_cliente}` : 'Sem CPF/CNPJ vinculado'}
                        <UcsDoCard d={d} />
                        {' · '}desde {formatDia(d.referencia_em)}
                        {d.prazo_em ? <> · prazo {formatDia(d.prazo_em)}</> : null}
                        {/* Cobrança do cliente: o dono é o da carteira (ou o da etapa,
                            na régua). Documento: quem assumiu pela esteira. */}
                        {doCliente
                            ? (nomeDoDono
                                ? <> · resp. <strong>{nomeDoDono}</strong>{d.dono_pelo_contato ? <span className="text-gray-500 dark:text-gray-400" title="Sem dono na carteira: considerado de quem registrou o último contato, desde que recente"> (pelo último contato)<span className="sr-only">: o cliente não tem dono na carteira, então aparece com quem registrou o último contato</span></span> : null}</>
                                : <> · <span className="text-amber-600 dark:text-amber-400 font-semibold">sem responsável</span></>)
                            : d.atribuicao?.responsavel_nome
                                ? <> · com <strong>{d.atribuicao.responsavel_nome}</strong></>
                                : <> · <span className="text-amber-600 dark:text-amber-400 font-semibold">ninguém assumiu</span></>}
                        {/* Nos documentos o responsavel_nome é quem GEROU — útil,
                            mas não é dono. */}
                        {!doCliente && d.responsavel_nome
                            ? <span className="text-gray-400"> · gerado por {d.responsavel_nome}</span>
                            : null}
                    </p>
                    {/* Etapa com outra pessoa no mesmo cliente (#6209): um card só,
                        e quem tem a etapa sabe que é dela. */}
                    {d.donos_etapas?.length ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {d.donos_etapas.map((o, i) => (
                                <span key={o.id}>
                                    {i ? ' · ' : ''}
                                    {o.filas.map(f => FILAS[f].titulo.split(' · ')[0]).join(', ')} com{' '}
                                    <strong className="text-gray-700 dark:text-gray-200">{o.id === usuarioId ? 'você' : (o.nome ?? 'outra pessoa')}</strong>
                                </span>
                            ))}
                        </p>
                    ) : null}
                </div>

                {d.valor != null && d.valor > 0 ? (
                    <div className="text-right shrink-0">
                        <div className="text-lg font-black text-primary">{formatBRL(d.valor)}</div>
                        {faturas.length ? (
                            <div className="text-[11px] text-gray-400">
                                {faturas.length} {faturas.length === 1 ? 'fatura' : 'faturas'}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {d.faturas_abertas?.length ? (
                <FaturasAbertas faturas={d.faturas_abertas} />
            ) : faturas.length ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {faturas.map(f => (
                        <span
                            key={f.fatura_id}
                            className="text-[11px] font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded"
                            title={`Fatura ${f.fatura_id}${f.vencimento ? ` · venc. ${formatDia(f.vencimento)}` : ''}`}
                        >
                            UC {f.uc_codigo} · {formatBRL(f.valor)}
                        </span>
                    ))}
                </div>
            ) : null}

            {/* Registro da execução, aviso do D+17 ou a instrução da fila */}
            {acao && !pendente ? (
                <div className="mt-3 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 rounded-lg p-2.5">
                    {acao.status === 'concluido' ? (
                        <>
                            <strong>{acao.executado_por_nome || 'Alguém'}</strong>
                            {acao.executado_em ? ` · ${new Date(acao.executado_em).toLocaleDateString('pt-BR')}` : ''}
                            {acao.observacao ? <p className="mt-1 text-gray-500 dark:text-gray-400">{acao.observacao}</p> : null}
                        </>
                    ) : (
                        <>
                            Dispensado{acao.executado_por_nome ? ` por ${acao.executado_por_nome}` : ''}
                            {acao.dispensado_motivo ? <p className="mt-1 text-gray-500 dark:text-gray-400">{acao.dispensado_motivo}</p> : null}
                        </>
                    )}
                </div>
            ) : estadoNotif.tipo === 'ja_notificada' ? (
                <div className="mt-3 text-xs text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30 rounded-lg p-2.5">
                    Já notificada em {formatDiaRegua(estadoNotif.enviadoEm)}
                    {estadoNotif.resultado ? ` — resultado: ${estadoNotif.resultado}` : ' — ainda dentro do prazo de verificação'}.
                    {' '}Não precisa de outra notificação: registre o desfecho em "Gerenciar negociação" ou dispense a etapa.
                </div>
            ) : estadoNotif.tipo === 'sem_cpf' ? (
                <div className="mt-3 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-lg p-2.5">
                    CPF/CNPJ não cadastrado nesta UC — não dá para saber se este cliente já foi notificado sob outro cadastro. Corrija o documento na UC antes de gerar uma notificação nova.
                </div>
            ) : (
                <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">{d.detalhe ?? meta.instrucao}</p>
            )}

            {/* Fatos que a fila precisa mostrar — modelo do distrato, motivo,
                quem mandou negativar, o que foi combinado. Sem isto a pessoa
                sai da tela para descobrir o básico do caso. */}
            {d.contexto?.length ? (
                <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
                    {d.contexto.map(({ rotulo, valor }) => (
                        <div key={rotulo} className="min-w-0">
                            <dt className="inline text-gray-400 dark:text-gray-500">{rotulo}: </dt>
                            <dd className="inline text-gray-700 dark:text-gray-200 font-medium">{valor}</dd>
                        </div>
                    ))}
                </dl>
            ) : null}

            {/* Último andamento — é o que responde "está sendo feito?" para
                quem olha a fila de fora. */}
            {d.atribuicao?.ultima_acao_em ? (
                <div className="mt-3 flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 bg-primary/5 dark:bg-primary/10 border-l-2 border-primary/40 rounded-r-lg p-2.5">
                    <div className="min-w-0 flex-1">
                        <strong>{d.atribuicao.ultima_acao_por_nome || 'Alguém'}</strong> anotou em{' '}
                        {formatDia(d.atribuicao.ultima_acao_em)}
                        {d.atribuicao.observacao
                            ? <p className="mt-1 text-gray-500 dark:text-gray-400 whitespace-pre-line break-words">{d.atribuicao.observacao}</p>
                            : null}
                    </div>
                    {/* Anotação preenchida errado (#6290) — quem escreveu, ou gestão. */}
                    {podeCorrigirAnotacao ? (
                        <div className="flex shrink-0 gap-0.5 -my-1 -mr-1">
                            {d.atribuicao.observacao ? (
                                <button
                                    onClick={onCorrigirAnotacao}
                                    disabled={emAcao}
                                    className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-primary/10 dark:hover:text-primary-light disabled:opacity-50 transition-colors"
                                    title="Corrigir o texto da anotação"
                                    aria-label="Corrigir anotação"
                                >
                                    <Pencil size={13} />
                                </button>
                            ) : null}
                            <button
                                onClick={onExcluirAnotacao}
                                disabled={emAcao}
                                className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                                title="Excluir a anotação"
                                aria-label="Excluir anotação"
                            >
                                {emAcao ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                            </button>
                        </div>
                    ) : null}
                </div>
            ) : null}

            <div className="mt-3 flex flex-wrap gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                {/* Ações da cobrança do cliente. Registrar o contato fecha as
                    etapas de contato do cliente para todo mundo e, com o próximo
                    contato, tira o cliente do painel até a data (#6147). */}
                {temRetirada ? (
                    <button
                        onClick={onRetirarNegativacao}
                        disabled={emAcao}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                        title="Registra que a negativação já foi retirada do SPC/Serasa"
                    >
                        <BadgeCheck size={13} /> Registrar retirada
                    </button>
                ) : null}

                {temContato ? (
                    <button
                        onClick={() => onRegistrar('contato')}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white hover:bg-primary/90 transition-colors flex items-center gap-1.5"
                        title="Data do contato, próximo contato e observação"
                    >
                        <CheckCircle2 size={13} /> Registrar contato
                    </button>
                ) : null}

                {temD17 && !jaNotificada ? (
                    // Era um link para a tela de UCs: o painel dizia "Gerar
                    // notificação" e mandava a pessoa embora. Agora o formulário
                    // abre aqui.
                    <button
                        onClick={onEmitirMora}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-orange-500 text-white hover:bg-orange-600 transition-colors flex items-center gap-1.5"
                        title="Abre o formulário da notificação extrajudicial. Esta etapa fecha quando você registrar o envio ao cliente."
                    >
                        <FileWarning size={13} /> {FILAS.regua_d17.acaoLabel}
                    </button>
                ) : null}

                {temD24 ? (
                    <button
                        onClick={() => onRegistrar('conferencia')}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors flex items-center gap-1.5"
                    >
                        <ShieldAlert size={13} /> {FILAS.regua_d24.acaoLabel}
                    </button>
                ) : null}

                {pendentesRegua.length ? (
                    <button
                        onClick={() => onRegistrar('dispensar')}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                    >
                        <Ban size={13} /> Dispensar
                    </button>
                ) : null}

                {acao && !pendente ? (
                    <button
                        onClick={() => onReabrir(acao)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                    >
                        <RotateCcw size={13} /> Devolver para a fila
                    </button>
                ) : null}

                {podeAssumirCliente ? (
                    <button
                        onClick={onAssumir}
                        disabled={emAcao}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50 disabled:cursor-wait transition-colors flex items-center gap-1.5 ${donoDaCarteira
                            ? 'text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/40 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                            : 'text-primary border border-primary/25 hover:bg-primary/10'}`}
                        title={d.dono_pelo_contato
                            ? 'Sem dono na carteira: hoje aparece com quem registrou o último contato. Assumir põe o cliente na sua carteira'
                            : dono
                            ? (minhaEtapa
                                ? `Você já tem a etapa deste cliente. Assumir tira a carteira inteira de ${nomeDoDono ?? 'outra pessoa'} — só use se for assumir o cliente todo`
                                : `Está com ${nomeDoDono ?? 'outra pessoa'} — assumir passa o cliente inteiro para você`)
                            : 'O cliente passa para a sua carteira e sai da fila de quem tinha'}
                    >
                        {emAcao ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />}
                        {donoDaCarteira ? 'Assumir cliente mesmo assim' : 'Assumir cliente'}
                    </button>
                ) : null}

                {/* Assumir / devolver / anotar nos documentos — o controle de
                    execução das filas derivadas. */}
                {podeAtribuirDocumento ? (
                    <>
                        {!dono ? (
                            <button
                                onClick={onAssumir}
                                disabled={emAcao}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-wait transition-colors flex items-center gap-1.5"
                            >
                                {emAcao ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Assumir
                            </button>
                        ) : meu ? (
                            <button
                                onClick={onLiberar}
                                disabled={emAcao}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-wait transition-colors flex items-center gap-1.5"
                                title="Devolver para a fila aberta"
                            >
                                {emAcao ? <Loader2 size={13} className="animate-spin" /> : <UserMinus size={13} />} Devolver
                            </button>
                        ) : (
                            <button
                                onClick={onAssumir}
                                disabled={emAcao}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/40 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50 disabled:cursor-wait transition-colors flex items-center gap-1.5"
                                title={`Está com ${d.atribuicao?.responsavel_nome ?? 'outra pessoa'} — assumir passa para você`}
                            >
                                {emAcao ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Assumir mesmo assim
                            </button>
                        )}

                        <button
                            onClick={onAndamento}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-primary border border-primary/25 hover:bg-primary/10 transition-colors flex items-center gap-1.5"
                        >
                            <NotebookPen size={13} /> Anotar andamento
                        </button>
                    </>
                ) : null}

                {/* Navegação/consulta — visualmente separada das ações de
                    execução acima. Empurrada para a direita: quem quer agir vê
                    primeiro o que muda o estado da demanda. */}
                <div className="flex flex-wrap gap-2 sm:ml-auto">
                    {/* A ação da fila de documento, executada AQUI — é o "trabalhar
                    nelas" do pedido. Cada fila tem a sua: cobrar a assinatura
                    quando falta assinar, registrar o envio quando já assinou. */}
                {d.fila === 'mora_sem_assinatura' || d.fila === 'advertencia_sem_assinatura' ? (
                    <button
                        onClick={onLembrarAssinatura}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white hover:bg-primary/90 transition-colors flex items-center gap-1.5"
                        title="Cobra a assinatura do procurador por e-mail e notificação no sistema"
                    >
                        <MailWarning size={13} /> Cobrar assinatura
                    </button>
                ) : null}

                {/* Distrato não passa pela edge function de lembrete (ela só
                    conhece mora e advertência), então a cobrança sai por e-mail
                    para exatamente quem falta — endereço copiado do Autentique.
                    Antes estes cards não tinham ação nenhuma: 78 distratos
                    travados sem jeito de cobrar a partir do painel. */}
                {(() => {
                    const alvo = destinatariosCobrancaDistrato(d);
                    if (!alvo) return null;
                    const assunto = `Assinatura pendente — distrato de ${d.cliente_nome || 'cliente'}${d.uc_numero ? ` (UC ${d.uc_numero})` : ''}`;
                    const corpo = alvo.interno
                        ? `Olá! O distrato de ${d.cliente_nome || 'cliente'} está aguardando a sua assinatura no Autentique há ${d.dias_parado} dias. Pode assinar hoje?`
                        : `Olá! Seu termo de distrato com a Vigor Energy está aguardando a sua assinatura. O link foi enviado para este e-mail pelo Autentique.`;
                    return (
                        <a
                            href={`mailto:${alvo.emails.join(',')}?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}`}
                            onClick={() => onRegistrarCobranca(`Cobrança de assinatura enviada por e-mail para ${alvo.emails.join(', ')}.`)}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white hover:bg-primary/90 transition-colors flex items-center gap-1.5"
                            title={`Cobrar ${alvo.nomes.join(', ')} por e-mail — fica registrado no andamento`}
                        >
                            <MailWarning size={13} /> Cobrar {alvo.interno ? alvo.nomes.join(', ') : 'cliente'}
                        </a>
                    );
                })()}

                {d.fila === 'mora_sem_envio' || d.fila === 'advertencia_sem_envio' || d.fila === 'distrato_assinado_sem_envio' ? (
                    <>
                        <button
                            onClick={() => onAcaoDoc('registrar_envio')}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white hover:bg-primary/90 transition-colors flex items-center gap-1.5"
                            title="Registra a data do envio — é ela que inicia o prazo"
                        >
                            <Send size={13} /> Registrar envio
                        </button>
                        <button
                            onClick={() => onAcaoDoc('nao_enviar')}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/40 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors flex items-center gap-1.5"
                        >
                            <MailWarning size={13} /> Não será enviado
                        </button>
                    </>
                ) : null}

                {ehDeDocumento(d) ? (
                    <button
                        onClick={() => onAcaoDoc('cancelar')}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-1.5"
                        title="Apaga no Autentique se ninguém assinou; anula se já houver assinatura"
                    >
                        <Ban size={13} /> Cancelar documento
                    </button>
                ) : null}

                {/* Contato vale para TODA fila, não só para a régua. O dado já
                    estava gravado na mora e na advertência e não aparecia: quem
                    ia cobrar a assinatura saía da tela para achar o número. */}
                {d.contato_whatsapp ? (
                    <a
                        href={`https://wa.me/55${d.contato_whatsapp.replace(/\D/g, '').replace(/^55/, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 transition-colors flex items-center gap-1.5"
                    >
                        <MessageCircle size={13} /> WhatsApp
                    </a>
                ) : null}

                {d.contato_email ? (
                    <a
                        href={`mailto:${d.contato_email}`}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                        title={d.contato_email}
                    >
                        <Mail size={13} /> E-mail
                    </a>
                ) : null}

                {d.url_documento ? (
                        <a
                            href={d.url_documento}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors flex items-center gap-1.5"
                        >
                            <FileText size={13} /> Abrir PDF
                        </a>
                    ) : null}

                    {ehDeDocumento(d) ? (
                        <a
                            href="#/financeiro/ucs"
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                            title="A ação do documento (registrar envio, cancelar, reenviar) vive no histórico da UC"
                        >
                            <ExternalLink size={13} /> Abrir na UC
                        </a>
                    ) : null}

                    {d.cpf_cnpj_cliente ? (
                        <button
                            onClick={onGerenciar}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors flex items-center gap-1.5 ${
                                jaNotificada
                                    ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700'
                                    : 'bg-purple-50 dark:bg-purple-900/10 text-purple-700 dark:text-purple-400 border-purple-100 dark:border-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/20'
                            }`}
                            title="Mudar status da negociação, assumir, negativar, transferir ou agendar retorno"
                        >
                            {/* O rótulo é sempre este: o botão abre o painel de
                                negociação, seja qual for a fila. Já usou
                                `meta.acaoLabel` aqui e mentia — numa fila de
                                documento ele lia "Abrir documento"/"Registrar
                                envio" e abria a negociação. A ação da fila é
                                feita pelos botões da esquerda. */}
                            <Users size={13} /> Gerenciar negociação
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default EsteiraCobrancaView;
